/**
 * ArkFloorPlan - Leaflet 平面图封装
 *
 * 坐标系：Leaflet CRS.Simple
 * - 原点：左下角 (0, 0)
 * - X 轴：向右为正 → Leaflet lng
 * - Y 轴：向上为正 → Leaflet lat
 * - setLatLng([Y, X]) 注意顺序
 *
 * v3.2 更新：
 * - ✅ 默认 zoom=0 原始尺寸显示，不自动缩放适应容器
 * - ✅ 1 画布单位 = 1 屏幕像素（zoom=0 时）
 * - ✅ 简化 scale 计算逻辑
 * - ✅ 边界约束直接使用画布坐标
 * - ✅ 编辑器和查看页面显示完全一致
 *
 * v3.3 更新：
 * - ✅ 修复 _createIcon 中 extraData 属性名大小写问题
 * - ✅ 使用 getProp 兼容 C# PascalCase 和 JS camelCase
 * - ✅ 修复台号锁定状态不更新的问题
 * - ✅ 所有锁定状态都显示（自己锁定=蓝色人形图标，他人锁定=灰色锁图标）
 * - ✅ 修复缩放时台号名称字体不同步的问题（使用内联样式）
 * - ✅ 新增显示控制配置：showAmount, showDuration, showGuestCount
 *
 * v3.4 更新：
 * - ✅ 修复解锁状态不更新：updateTableStatus 改为完全替换 extraData
 * - ✅ 统一锁定图标：所有锁定使用相同的醒目红色锁图标
 *
 * v3.5 更新：
 * - ✅ 彻底修复解锁状态不更新问题：
 *   - 显式初始化 extraData 默认值（locked: false 等）
 *   - 使用严格比较 === true 判断锁定状态
 *   - 确保即使 JSON 序列化忽略 false 值也能正确处理
 *
 * v3.6 更新：
 * - ✅ 修复 Android/Windows 缩放限制失效问题：
 *   - 统一所有平台 zoomSnap: 0，避免 snap 对齐导致无法到达 min/max 边界
 *   - 降低 maxBoundsViscosity 至 0.8，减少边界约束对 pinch-to-zoom 的干扰
 * - ✅ 缩放丝滑优化：
 *   - zoomanim 事件中对 marker 应用 CSS transform scale 平滑过渡
 *   - zoomend 中重建图标匹配最终缩放级别
 *   - 缩放过程中桌台图标跟随地图平滑缩放，体验接近原生
 *
 * v3.36 更新（单层变换缩放引擎 —— 修安卓捏合「不跟手 / 起手松手顿 / 硬墙」）：
 * - ✅ 手势与视图过渡期间只给 .leaflet-map-pane 写 translate3d(T) scale(s)，不碰 Leaflet 状态；
 *   画面静止后一次 setView 写回（_zlCommit）。每帧 1 次 DOM 写入，与桌台数无关。
 * - ✅ 锚点改为「起手时捏住的内容」：v3.29 每帧取当前两指中点下的内容，两指一起平移时
 *   画面按 −(s−1) 倍反向走（2026-10-16 生产 bundle 实测：2 倍下右移 100px，桌台左移 100px）。
 * - ✅ 起手不再调公开 map.stop()（zoomSnap:0 下会触发 moveend → OnViewChanged 跨 .NET + viewreset 全图层重算）。
 * - ✅ 缩放上下限改为阻力（_zlRubber），平移手势期不夹紧；松手以最后指位为锚，按 --ark-dur-base /
 *   --ark-ease-enter 回到边界。抬起一指可用剩下那根接着拖。
 * - ✅ 双击 / resetView / focusTable / zoomIn / zoomOut / setZoom / fitToContainer 统一走本引擎（对数插值）。
 * - ✅ OnViewChanged / OnZoomChanged 防抖 300ms 合并发送（手势中不跨 .NET）；点桌台、画布外按下时先补发。
 *
 * v3.35 更新（两轴对称拖出 —— maxBoundsViscosity 1.0→0）：
 * - ✅ 修"上下能拖出父元素、左右不能"的不对称:根因是自定义 FlippedSimpleCRS(Y 向下)破坏了
 *   Leaflet Map.Drag._onDragStart 的粘滞限位 _offsetLimit 计算(该公式假设北在上)——纵向限位
 *   退化成超大范围≈不夹(能自由拖出),横向不受翻转影响被正确夹死(拖不出)。viscosity=0 令
 *   _offsetLimit=null 两轴都不夹 → 一致「自由拖出 + 松手 _panInsideMaxBounds 弹回」。inertia
 *   保持 false;回弹 _panAnim 由 v3.33 pointerdown 停 pan 监听兜底。
 *
 * v3.34 更新（v3.31~v3.33 真机全败后改路线 —— 直接移除 pan 动画源）：
 * - ✅ inertia:false + maxBoundsViscosity:1.0。放弃"抢在 Leaflet _onDown 前 stop 动画"的事件时序
 *   博弈（三次都因 pointer/touch 事件时序脆弱而在真机失败），改为直接去掉会产生 pan 动画的两个源：
 *   惯性滑行(inertia) 与 松手回弹(viscosity<1)。无 pan 动画 = _onDown 永远采不到动画中途基准 =
 *   反向滑动不可能偏离，与事件时序无关。副作用信号：松手即停不再滑行 = 新 bundle 已加载的可见判据。
 *   代价：失去甩动滑行手感（POS 地板可接受）。v3.33 的 pointerdown 停 pan 监听保留作兜底(程序化动画)。
 *
 * v3.33 更新（修 v3.32 仍不生效 —— 真正根因：Leaflet 用 pointerdown 驱动拖动）：
 * - ✅ 「按下停惯性」监听补上 **document.capture 的 pointerdown**（原只监听 touchstart/mousedown）。
 *   现代 WebView 支持 PointerEvent，Leaflet 经 DomEvent.Pointer 把 Draggable 的 touchstart
 *   转译为 container 上的 pointerdown(bubble)，拖动实际由 pointerdown 驱动；v3.31/v3.32 监听
 *   touchstart 在 pointer 设备上是错误事件，pointerdown 的 bubble 阶段 _onDown 早已采完陈旧
 *   _startPos。改监听 pointerdown 后，document.capture 严格早于 container.bubble 的 _onDown
 *   （同一次分发 capture→target→bubble），保证先 stop 再采样，与注册先后无关。保留惯性手感。
 *   （注：v3.32 注释里"AT_TARGET 注册序"是当时的错误推断，真正原因是事件类型 touchstart ≠ pointerdown。）
 *
 * v3.32 更新（修 v3.31 未真正生效 —— 反向滑动仍偏离）：
 * - ✅ 「按下即停惯性」监听从 container.capture 上移到 document.capture。
 *   根因：空白地板 touchstart 目标就是 container 本身（CRS.Simple 无瓦片，leaflet-pane
 *   收缩为 0×0 不覆盖容器）；目标 === container 时处于 AT_TARGET 阶段，capture/bubble
 *   监听按注册先后触发，Leaflet 的 Draggable._onDown 在 L.map() 里先注册 → 先跑 → 我们
 *   的 stop 后跑，采到的 _startPos 仍是陈旧基准。祖先节点 document 的 capture 阶段严格早于
 *   目标节点任何监听，保证在 _onDown 之前 stop 掉惯性。用 _container.contains 限定作用域。
 *
 * v3.31 更新（按下即停惯性 —— 修 POS「惯性动画将尽时反向滑动出现偏离」）：
 * - ✅ 容器 touchstart/mousedown capture 阶段监听：_panAnim 进行中立即 map._stop()
 *   （iOS/Google Maps「touch catches the pan」同款手感）。
 * - 根因：Leaflet 1.9.4 Draggable._onDown（touchstart）采样 _startPos 基准后，到 dragstart
 *   （移动 clickTolerance 3px）之间 PosAnimation 仍每帧推进 _mapPane（甚至自然跑完全程）；
 *   dragstart 起 Draggable 以陈旧基准写位置，pane 从动画终点瞬间跳回按下点，
 *   回跳量 = 按下后动画多走的距离。easeOut 尾巴慢而长 →「动画快结束时按下」回跳最大；
 *   动画完全停稳后再滑则基准一致无偏移，与复现时序完全吻合。
 *   （_onDown 只防 leaflet-zoom-anim，对 pan 动画无等效防御 —— Leaflet 自身盲区。）
 * - capture + 同步 stop：同一事件分发内动画已停，基准永远新鲜；passive:true 不碰
 *   preventDefault，点击 marker / pinch 语义不变。用 map._stop() 而非公开 stop() ——
 *   后者在 zoomSnap:0 下额外 fire('viewreset') + setZoom，纯 pan 场景多余的全图层重投影。
 *
 * v3.30 更新（贴墙夹紧 + 吸附优先级重排 —— 修"两桌靠不到允许的最近距离"）：
 * - ✅ 硬墙回退改"夹紧到接触点"：新增 _clampToContact（from→to 线段 8 次二分逼近，
 *   形状无关统一走 _checkCollision）+ _slideToContact（先沿运动方向夹紧再轴向滑墙 X 优先）；
 *   单选硬墙 / 组拖硬墙（_clampGroupOffsetToContact）/ dragend 兜底链首三处接入。
 *   修前根因：撞墙时回退上一帧采样位置，最终间距=上一帧空隙（快拖单帧位移 20~50px），
 *   贴边只能靠磁吸带碰运气；修后推到底=停在允许的最近位置（CollisionPadding=0 即贴边）。
 * - ✅ 磁吸先于网格吸附计算（对象对齐 > 网格对齐，Figma 同款优先级）；已磁吸命中的轴
 *   跳过网格吸附。修网格阈值(12) > 磁吸阈值(8) 倒挂产生的 8~12px 死区
 *   （贴边点落在网格点 8~12px 环内时，从网格侧接近永远被拽回网格点）。
 * - ✅ 磁吸"贴边"配对（left↔right / top↔bottom）要求正交投影邻近（间隙 ≤ 1.5×较大尺寸）
 *   才参与竞争，且距离×0.8 权重 —— 防画布另一头桌台的对齐线劫持贴边磁吸。
 * - ✅ 磁吸/网格吸附阈值按屏幕像素语义随 zoom 换算（_snapThresholdCanvas），
 *   50%~150% 缩放下吸附手感一致（旧版按画布单位，缩小时等效阈值折半）。
 * - ✅ snapThreshold 默认 8 → 12（与网格阈值持平；Razor 参数 / FloorPlanOptions 同步）。
 * - ✅ dragend 取整后复检碰撞，亚像素重叠在 1px 邻位修正（_nudgeRoundedSafe）——
 *   杜绝奇数尺寸贴边中心 x.5 被 round 出 0.5px 重叠入库、漂移后续碰撞基准。
 *
 * v3.27.3 更新（修组级硬墙忽略画布边界）：
 * - ✅ _groupOffsetCollides 新增"边界裁剪也算撞墙"判据：任一成员的目标位置
 *   (origin + dx/dy) 被 _constrainPosition 拉回（c !== proposed）→ 立即返回 true
 * - 修前 bug（截图复现）：选中 4 个，按住 T012 向上拖，T08 起点最靠上 → T08
 *   先撞顶边，但 _groupOffsetCollides 只检查 marker-vs-marker 碰撞，T08 被画布
 *   静默裁回 → 组继续推进 → T011 滑到 T08 旁边视觉重叠，直到 T012 自己被
 *   step1 的 _constrainPosition 卡顶才停。修后 T08 触顶即组级硬墙，T012 在中途
 *   停下，整组无内部堆叠。
 *
 * v3.27.2 更新（回退 v3.27.1 的 Leaflet 内部状态 hack）：
 * - ❌ v3.27.1 在 drag 末尾每帧偏移 _startPos / _newPos 看似优雅，但 grid snap
 *   阈值 12px 会让每帧产生小 delta，delta 累加到 _startPos 上后慢拖永远卡在 snap 点
 *   （需要单帧鼠标位移 > 12px 才能脱出）。用户报告"严重依赖吸附 / 卡顿"。
 * - ✅ 重新核对 Leaflet 执行顺序：_updatePosition 内 ① DomUtil.setPosition(_newPos)
 *   ② fire('drag') → 我们的 handler ③ setLatLng(safe)，①③ 同 rAF 回调内同步执行，
 *   浏览器只 paint 最终的 safe；dragend 的 _onUp 会 cancelAnimFrame 取消最后一次
 *   pending render，marker._latlng 保留 safe。无需操作 Leaflet 内部状态。
 * - 注：CollisionPadding=0（FloorPlanEditor.razor 的设置）下，两个 marker "贴边触碰"
 *   边线相邻、视觉几乎重叠 —— 这是合法判据（< 严格不成立 = 不算碰撞），不是 bug。
 *   若希望可见间距，把 CollisionPadding 调到 4-5。
 *
 * v3.27.1 更新（已回退）：
 * v3.27.14 更新（双模式精致边线 + 4 角）：
 * - ✅ _createBoundary 拆为 4 条独立边线 polyline + 4 角各 2 段（共 8 段）；
 *   每段加 className 后缀（top/right/bottom/left/tl/tr/br/bl）供 CSS 精细控制
 * - ✅ 编辑模式：CSS 隐藏 top/left 边线 + tl/tr/bl 3 角，仅留右+下边 + 右下角（与 resize 匹配）
 * - ✅ 查看模式（POS 客户端）：4 边精致实线（slate-400 + 1.5px）+ 4 角 L 形（slate-600 + 2px）
 *   颜色低调克制，不抢桌台焦点；用户能清楚识别画布范围
 *
 * v3.27.13 更新（单一通路加载 Tables）：
 * - ✅ InitializeAsync 不再内嵌 AddTablesAsync，所有 Tables 加载经 RefreshTablesAsync 单一通路
 * - 修首次打开桌台管理页画布空白 race（v3.27.12 兜底失败重做）
 *
 * v3.27.11 更新（查看模式隐藏网格）：
 * - ✅ CSS 加 .ark-floor-plan:not(.editable) .ark-floor-plan-grid { display: none } —
 *   POS 客户端等只读场景完全隐藏网格，避免抢桌台状态戏。与 v3.10 既有的
 *   "查看模式淡化边线 + 隐藏角标" pattern 保持一致语义
 *
 * v3.27.10 更新（双层网格背景）：
 * - ✅ _createGrid 由点阵改为 SVG 双层 pattern：4px 小网格做底纹（极淡，仅作刻度参考）
 *   + 80px 大网格做主参考（清晰）；网格密集铺满画布 → 画布边界自然显现
 * - 视觉风格类似 CAD/Figma artboard，与点阵相比更专业、空间感更强
 *
 * v3.27.9 更新（已弃用，由 v3.27.10 取代）：
 *
 * v3.27.8 更新（画布边线视觉与 resize 功能匹配）：
 * - ✅ _createBoundary 由 4 边闭合虚线 + 8 段 L 角标 → 右+下 开放 L 虚线 + 仅右下 L 角标
 *   左/上是基准侧（原点固定不可调）不画线；右/下是可扩展侧（v3.23 hover/拖动支持）保留虚线
 *   右下角 L 角标强调主拖动点；视觉信号 100% 匹配 resize 功能
 *
 * v3.27.7 更新（清退 alignTables / nudgeTables）：
 * - ❌ 删除公共方法 alignTables / nudgeTables 及全局 API、OnTablesAlignFailed 事件
 *   POS 编辑场景：拖动 + 网格 snap 已覆盖 80% 对齐诉求；对齐 6 + 微移 4 按钮
 *   触屏使用率极低、增加视觉负担；多选浮条简化为"状态提示 + 退出口"。
 * - ✅ 组级硬墙核心保留：_groupOffsetCollides / _findNearestSafeGroupOffset /
 *   _hasCollisionWithOutsideGroup / _dragLastSafeGroup —— 拖动场景仍依赖。
 *
 * v3.27 更新（批量移动防碰撞）：
 * - ✅ 组级硬墙：drag 期间整组按主 marker 的 dx/dy 同步移动前，先用
 *   _groupOffsetCollides 校验任一成员是否撞"组外"marker；撞则尝试
 *   (dx, last.dy) → (last.dx, dy) → 都堵保持 last，硬墙后的 dx/dy 反写主 marker
 * - ✅ dragend 三级兜底：_findNearestSafeGroupOffset → _dragLastSafeGroup → {0,0}
 *   全堵则整组回到拖动起点，不再"穿透写入数据库"
 * - ✅ 磁吸协同：组拖动时 snapWouldCollide 改用 _groupOffsetCollides（整组测试）
 *
 * v3.26 更新（翻转 CRS Y 轴 + resize 不重置视图中心）：
 * - ✅ 自定义 FlippedSimpleCRS：transformation (1,0,1,0) 让 lat 与屏幕 Y 同方向（向下递增）
 *   PosTable.Y=0 在屏幕顶（与用户直觉一致）；[H,W] 是屏幕右下；[0,0] 是屏幕左上
 *   marker / boundary / grid / background 自动按新 CRS 投影；业务逻辑用 (X, Y) 0~W/H 不变
 * - ✅ updateBounds 加 keepView 参数，resize 拖动期间传 true 不重置 setView 中心
 *   桌台屏幕位置固定，画布扩展全部发生在右/下方向（直觉一致）
 * - ✅ resize hover 检测改回 [H,W]/[0,0] 直觉投影
 * - ✅ _onResizeMove 用 point.lat / point.lng 简化
 *
 * v3.25 更新（修 v3.24 上下/右下方向反转 bug）：
 * - ✅ 修 hover 检测：leaflet CRS.Simple Y 向上，[H,W] 是屏幕右上而非右下，
 *   改用 [0, W]（屏幕右下）和 [H, 0]（屏幕左上）正确投影
 * - ✅ 修拖动方向：用屏幕像素差 / scale 计算 W/H 增量（向右 → W 增；向下 → H 增）
 *   不再用 point.lat 反映 leaflet Y 向上语义
 *
 * v3.24 更新（边线本体拖动 + 浮标移左上角）：
 * - ✅ 取消 3 个 handle DOM；改为容器 mousemove 检测鼠标 vs 画布右/下/右下边缘距离（HIT_PX=8）
 *   接近边缘时容器 cursor 切换为 ew-resize / ns-resize / nwse-resize；mousedown 启动 resize
 *   _resizeHover 跟踪当前 hover 区；_resizing 期间锁定不重新检测
 * - ✅ 拖动尺寸浮标从右下角移到左上角（left:16px top:16px）；样式与信息栏一致
 *   （bg surface/60 + 4px 圆角 + text-xs + tabular-nums）；拖动期间覆盖信息栏
 *
 * v3.23 更新（画布边界拖动 resize）：
 * - ✅ _createResizeHandles 在画布右下/右/下 3 个边界点放置 8x8 div handle
 *   屏幕坐标由 leaflet latLngToContainerPoint 投影；zoom/move/updateBounds 时由 _repositionHandles 同步
 * - ✅ _onResize{Start,Move,End}：拖动期间实时调 updateBounds + 浮标显示 W × H
 * - ✅ 越界保护：minW/minH = max(table.X + W/2) + 80px 余量；handle 卡住
 * - ✅ snap：与桌台拖动共用 _altPressed 临时禁用；默认 80px 网格吸附
 * - ✅ destroy / setEditable 切换时同步 handle 创建/移除
 *
 * v3.20 更新（取消修饰键 + 修 mouseup 后清空 bug）：
 * - ✅ 修框选 mouseup 后浏览器合成 click 冒泡到 map.click 触发清空选中：
 *   _suppressMapClickUntil 200ms 时间窗跳过本次合成 click
 * - ✅ 简化多选触发条件：仅靠 _persistentMultiMode 开关；取消 Shift/Ctrl/Cmd 修饰键路径
 *   （配合 .NET 端取消 ArkShortcuts 注册 = 完全无键盘交互）
 *
 * v3.19 更新（实时框选）：
 * - ✅ _onRubberbandMove 内每帧调 _updateRubberbandSelection 实时计算选区与 marker AABB 重叠
 *   → 选中态实时更新（Windows 资源管理器风格 marquee），无需等 mouseup
 * - ✅ baseSet 记录框选起点的已选集合，union 模式下保留 base + 新框中
 * - ✅ _setEquals 防抖：仅当集合真正变化才刷新视觉
 *
 * v3.18 更新（平移修复 + 持续多选模式）：
 * - ✅ 修 v3.15 引入的左键 pan regression：拖框选仅在 Shift/Ctrl/Cmd 或 持续多选模式 触发
 * - ✅ 持续多选模式 _persistentMultiMode：marker click / 拖框选都默认走多选语义，无需按 Ctrl
 * - ✅ 全局 API ArkFloorPlan.setPersistentMultiMode(id, enabled)
 *
 * v3.16 更新（网格 snap-to-grid + Alt 临时禁用）：
 * - ✅ DEFAULTS 加 snapToGrid: true / snapToGridThreshold: 12
 * - ✅ drag 内边界约束之后、磁吸之前：吸附到 gridSize（默认 80）网格点（阈值内）
 * - ✅ window keydown/keyup 跟踪 Alt 键 → _altPressed 期间 snap 临时禁用
 *
 * v3.15 更新（多选 + 拖框选 + 多选拖动联动）：
 * - ✅ selectedTableId 单字段 → selectedTableIds Set；selectTable(id) 转 setSelectedTables([id])
 * - ✅ marker click 支持 Shift/Ctrl/Cmd 累加；OnTableClicked 回调附加 isMulti / selectedIds
 * - ✅ 拖框选：编辑模式下 map 容器 mousedown 非 marker → 启动选区矩形，mouseup 计算 AABB 重叠纳入选中
 * - ✅ 多选拖动联动：mousedown 时记 _dragGroupOrigin（组内每 marker 原位置），drag 内整组按位移同步 setLatLng
 * - ✅ 多选 dragend → 发 OnTablesMoved 批量回调（替代单个 OnTableMoved）
 * - ✅ map.click 空白 → 清空选中
 *
 * v3.13 更新（拖动硬墙 + 轴向滑墙）：
 * - ✅ drag 期间实时阻挡碰撞：理想位置碰撞时尝试轴向滑动（X 优先 / Y 次之 / 都堵原地）
 *   取代 v3.12 的"软碰撞 + 红框警告"，参考 Sweet Home 3D / Planner 5D 物理类比
 * - ✅ 沿邻居边滑墙缓解"撞到就停"的脱手感
 * - ✅ 红框警告（.collision class）保留 CSS 但 drag 中永不触发
 * - ✅ dragend 三级兜底（_findNearestSafePosition → _dragLastSafe → _dragOriginalPos）保留作防御
 *
 * v3.12 更新（防碰撞综合改进）：
 * - ✅ 形状感知碰撞：圆-圆走圆心距、圆-矩形走最近点距离、矩形/菱形保持 AABB（旋转字段仍忽略）
 * - ✅ drag 期间记录 _dragLastSafe（最后一次无碰撞位置），dragend 兜底链路：
 *   _findNearestSafePosition → _dragLastSafe → _dragOriginalPos，不再"找不到就甩回起点"
 * - ✅ findSafePosition / _findNearestSafePosition 搜索半径自适应画布对角线（原 5 环 / 100px）
 * - ✅ 磁吸前验证目标是否会碰撞，会碰则放弃磁吸（不再"吸到红框")
 *
 * v3.11 更新：
 * - ✅ addTables 加 dedupe 防御：同 id 已存在则先 removeTable，杜绝并发 RefreshTables 引发的游离 marker 累积
 *   （配合 ArkFloorPlan.razor RefreshTablesAsync 末尾同步 _lastXxxRef 双重保险）
 *
 * v3.10 更新：
 * - ✅ 画布对齐网格（点阵）：showGrid + gridSize 配置；svgOverlay 在画布坐标系绘制 SVG pattern，跟随地图缩放
 * - ✅ boundary corners 加 className 'ark-floor-plan-corner'，CSS 可在 :not(.editable) 下隐藏角标 / 淡化边框
 *   实现编辑模式（虚线 + 8 角标）vs 查看模式（淡薄实线）差异化视觉
 *
 * v3.9 更新（性能 / 安全 / 体验综合收口）：
 * - ✅ marker iconSize 锁定为 base 尺寸，缩放仅写 CSS 变量 --scale，浏览器 transition 平滑过渡
 *   缩放、selectTable、updateTableStatus 全部改走 _applyVisualState（仅改属性），不再 setIcon 重建 DOM
 * - ✅ 锁徽章 / QR 徽章 / table-extra 改为常驻子节点，状态切换走 [hidden] / data-* 属性
 * - ✅ addTables 去掉无引用的 L.layerGroup（避免 map._layers 累积空 group）
 * - ✅ _scheduleInvalidate 调换顺序：先 setView 再 invalidateSize，消除首帧两次 layer 重定位的视觉跳动
 * - ✅ _createIcon 内桌台号 / 锁定者 / 金额等外部串走 escapeHtml，杜绝 XSS / 属性注入
 */

import L from 'leaflet';
import { buildBackdropSvg, paintSwatches } from './ark-floor-plan-materials.js';
import { createDecorSvg, renderDecor } from './ark-floor-plan-decor.js';
import { setDecorEditMode, selectDecor } from './ark-floor-plan-decor-edit.js';

// ==================== 默认配置 ====================
const DEFAULTS = {
    width: 1920,
    height: 1080,
    minZoom: -2,
    maxZoom: 2,
    defaultZoom: 0,      // 默认 zoom=0，原始尺寸
    zoomSnap: 0.25,
    zoomDelta: 0.5,
    editable: false,
    snapThreshold: 12,   // v3.30: 8 → 12 与网格吸附阈值持平（贴边捕获带更宽）
    showBoundary: true,
    boundaryColor: '#3b82f6',
    boundaryWidth: 3,
    defaultTableW: 80,
    defaultTableH: 80,
    enableCollision: true,
    collisionPadding: 5,
    // 图标缩放限制
    minIconScale: 0.25,
    maxIconScale: 4,
    // 桌台信息显示控制
    showAmount: true,       // 显示订单金额
    showDuration: true,     // 显示用餐时长
    showGuestCount: false,  // 显示就餐人数
    kitchenReadyText: '出齐',     // 上菜状态「出齐」胶囊文案（C# FloorPlanOptions 传入覆盖）
    kitchenPendingText: '制作中', // 上菜状态「制作中」胶囊文案
    // 预订信息显示控制 —— showBookingBadge 刻意独立于 showDuration：
    // 历史上 📅 借道 duration 字段渲染，门店关掉「显示用餐时长」会让预订标记一并消失
    showBookingBadge: true,      // 显示预订时刻 📅 HH:mm（多笔逐行 + `+N`）
    showBookingName: true,       // 显示预订客人姓名（仅单笔临近预订）
    showBookingPhoneTail: false, // 显示手机尾号 4 位（附在姓名后）
    // 预订行在缩小到此 scale 以下时整体收起（11px 字乘以 0.7 已接近可读下限）
    bookingDensityMinScale: 0.7,
    // v3.10: 画布对齐网格（点阵）
    showGrid: false,
    gridSize: 80,
    // v3.16: 网格 snap-to-grid（拖动时吸附到网格点；按 Alt 临时禁用）
    snapToGrid: true,
    snapToGridThreshold: 12,
    // 2026-10-18: 地面素材档位名（null / 'none' = 不铺）
    backdrop: null,
    // 2026-10-18: 布景条目（C# FloorPlanDecorRenderItem[]；null / [] = 无）
    decor: null
};

// ==================== 工具函数 ====================

function getProp(obj, ...names) {
    for (const name of names) {
        if (obj[name] !== undefined) return obj[name];
        const camel = name.charAt(0).toLowerCase() + name.slice(1);
        if (obj[camel] !== undefined) return obj[camel];
        const pascal = name.charAt(0).toUpperCase() + name.slice(1);
        if (obj[pascal] !== undefined) return obj[pascal];
    }
    return undefined;
}

// v3.26: 自定义 CRS —— L.CRS.Simple 默认 transformation (1,0,-1,0) 让 lat 向上递增（数学坐标），
// 但与屏幕 Y（向下递增）相反，导致拖动 / 边界 / 桌台坐标语义错位。
// 改为 (1,0,1,0) 让 lat 与屏幕 Y 同方向，PosTable.Y=0 在屏幕顶 = 用户直觉。
const FlippedSimpleCRS = L.extend({}, L.CRS.Simple, {
    transformation: new L.Transformation(1, 0, 1, 0)
});

// v3.36: 缩放引擎的过渡曲线 = --ark-ease-enter（ark-motion.css）cubic-bezier(.2,0,0,1)
function cubicBezier(x1, y1, x2, y2) {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const sx = (t) => ((ax * t + bx) * t + cx) * t;
    const sy = (t) => ((ay * t + by) * t + cy) * t;
    return (x) => {
        if (x <= 0) return 0;
        if (x >= 1) return 1;
        let lo = 0, hi = 1, t = x;
        for (let i = 0; i < 24; i++) {
            t = (lo + hi) / 2;
            if (sx(t) < x) lo = t; else hi = t;
        }
        return sy(t);
    };
}
const easeEnter = cubicBezier(0.2, 0, 0, 1);

// v3.36: 缩放越界阻力的渐近上限（缩放级）—— 捏过上下限最多再多出约 0.35 级（±27%）
const ZOOM_RUBBER = 0.35;
// v3.36: OnViewChanged / OnZoomChanged 合并发送的静默期 —— 连续手势之间不跨 .NET
const VIEW_NOTIFY_DELAY = 300;

function normalizeTable(table) {
    return {
        id: String(getProp(table, 'id', 'Id') ?? ''),
        code: getProp(table, 'code', 'Code') ?? '',
        x: getProp(table, 'x', 'X') ?? 0,
        y: getProp(table, 'y', 'Y') ?? 0,
        w: getProp(table, 'w', 'W') ?? DEFAULTS.defaultTableW,
        h: getProp(table, 'h', 'H') ?? DEFAULTS.defaultTableH,
        shape: getProp(table, 'shape', 'Shape') ?? 0,
        capacity: getProp(table, 'capacity', 'Capacity') ?? 0
    };
}

// v3.9: 所有外部串入 innerHTML / 属性前必须过这里
// 锁定 / 账单已打印角标的「光晕」新样式（ShopSettings.Display.TableView.HaloStatusBadges 试用开关）。
// 两套图形都常驻 DOM（.bd-disc 原底圆内的白图标 / .bd-halo 本组），由容器 [data-badge-style] 经 CSS 择一显示，
// 切换开关无需重建 marker。字形与 TablePlanSelector.RenderGridTile 的 HaloLockSvg / HaloPrinterSvg 是同一份，改要两处同步。
// 光晕 = 同一图形画两遍（底层 .h 白粗线 / 上层 currentColor 细线）；实心锁身靠 paint-order 让白描边垫在填充之下。
// 锁 18px：光晕每边约 0.9px（红锁落红/琥珀台要靠它显形）；打印机与锁同为 18px：线宽约 1.9px、光晕每边 1px —— 部分门店以它作「客人已要账单、待买单」的服务信号，须与锁同档醒目。
const HALO_LOCK_SVG = '<svg class="bd-halo" viewBox="0 0 24 24" aria-hidden="true">'
    + '<path class="h" d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke-width="4.65" stroke-linecap="round"/>'
    + '<rect class="hb" width="18" height="11" x="3" y="11" rx="2.5" stroke-width="2.4" paint-order="stroke"/>'
    + '<path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round"/>'
    + '<rect class="kh" x="11" y="14.5" width="2" height="4" rx="1"/></svg>';
const HALO_PRINTER_PATHS = '<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>'
    + '<path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"/><rect x="6" y="14" width="12" height="8" rx="1"/>';
const HALO_PRINTER_SVG = '<svg class="bd-halo" viewBox="0 0 24 24" aria-hidden="true">'
    + `<g class="h" fill="none" stroke-width="5.17" stroke-linecap="round" stroke-linejoin="round">${HALO_PRINTER_PATHS}</g>`
    + `<g fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${HALO_PRINTER_PATHS}</g></svg>`;

// 平面图预订标记（仅图标模式，TablePlanSelector 根节点 data-map-booking="icon"）：左下角 18px 日历线条图标 + 白光晕，
// 与锁 / 打印机光晕同一做法；圆桌 / 菱形桌的左下角在桌形之外，靠光晕在任意画布上显形。
// 时刻 / +N / 姓名一律不在平面图出现（点开桌台看）；预订页（data-map-booking="full"）仍走下面的 .table-btime / .table-booking。
const HALO_CAL_PATHS = '<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>';
const HALO_CAL_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + `<g class="h" fill="none" stroke-width="5.17" stroke-linecap="round" stroke-linejoin="round">${HALO_CAL_PATHS}</g>`
    + `<g fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${HALO_CAL_PATHS}</g></svg>`;

// 部分支付标记：台号顶部正中的货币底层图标（规格同扫码底层图标：32px、20%、currentColor，见 ark-floor-plan.css .table-paid）。
// 币种由 TablePlanSelector 根节点 data-paid-icon 给出（C# 的 PaidIcon.Token），这里只按名取字形；缺省（Admin 编辑器）走通用货币符号。
// 路径照抄 Ark.UI/Assets/LucideIcons.cs 的 Badge* 系列与 Currency，改要两处同步。
const PAID_BADGE_OUTLINE = '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/>';
const PAID_ICON_PATHS = {
    yen: PAID_BADGE_OUTLINE + '<path d="m9 8 3 3v7"/><path d="m12 11 3-3"/><path d="M9 12h6"/><path d="M9 16h6"/>',
    dollar: PAID_BADGE_OUTLINE + '<path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/>',
    euro: PAID_BADGE_OUTLINE + '<path d="M7 12h5"/><path d="M15 9.4a4 4 0 1 0 0 5.2"/>',
    pound: PAID_BADGE_OUTLINE + '<path d="M8 12h4"/><path d="M10 16V9.5a2.5 2.5 0 0 1 5 0"/><path d="M8 16h7"/>',
    rupee: PAID_BADGE_OUTLINE + '<path d="M8 8h8"/><path d="M8 12h8"/><path d="m13 17-5-1h1a4 4 0 0 0 0-8"/>',
    ruble: PAID_BADGE_OUTLINE + '<path d="M9 16h5"/><path d="M9 12h5a2 2 0 1 0 0-4h-3v9"/>',
    other: '<circle cx="12" cy="12" r="8"/><line x1="3" x2="6" y1="3" y2="6"/><line x1="21" x2="18" y1="3" y2="6"/><line x1="3" x2="6" y1="21" y2="18"/><line x1="21" x2="18" y1="21" y2="18"/>'
};
const paidIconSvg = (token) => '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + (PAID_ICON_PATHS[token] ?? PAID_ICON_PATHS.other) + '</svg>';

const HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, ch => HTML_ESCAPE_MAP[ch]);
}

// v3.9: 默认 extraData，避免 spread 时 undefined 字段污染
function makeNormalizedExtra(rawExtraData) {
    return {
        locked: false,
        lockedByMe: false,
        lockedBy: null,
        amount: null,
        duration: null,
        guestCount: null,
        hasOnlineOrder: false,
        billPrinted: false,
        ...rawExtraData
    };
}

// ==================== FloorPlan 类 ====================
class FloorPlan {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.config = { ...DEFAULTS, ...options };
        this.map = null;
        this.markers = new Map();
        this.backgroundLayer = null;
        this.boundaryLayer = null;
        this.backdropLayer = null;
        this.decorLayer = null;
        this._decorSvg = null;
        this.boundaryLines = [];
        this.boundaryCorners = [];
        this.dotnetRef = null;
        // v3.15: 单字段 selectedTableId → Set。多选时含多个 id；保留 selectedTableId 作为"主选中"（最后点击的那个）兼容现 drawer 单选 UI
        this.selectedTableIds = new Set();
        this.selectedTableId = null;
        // v3.18: 持续多选模式（工具栏开关），开启时所有点击/拖框默认走多选语义，无需 Ctrl
        this._persistentMultiMode = false;
        this._dragStart = null;
        this._dragOriginalPos = null;
        this._alignmentGuides = [];
        this._pendingGuideX = null;
        this._pendingGuideY = null;
    }

    // ========== 初始化 ==========

    init(dotnetRef) {
        this.dotnetRef = dotnetRef;
        const container = document.getElementById(this.containerId);
        if (!container) {
            console.error(`[ArkFloorPlan] Container #${this.containerId} not found`);
            return this;
        }
        this._container = container;

        // v3.6: 统一所有平台 zoomSnap: 0
        // - 解决 Android pinch-to-zoom 因 snap 对齐无法到达 min/max 边界
        // - 解决 Windows 触屏设备被误判为触摸设备导致行为不一致
        // - 按钮缩放（zoomIn/zoomOut）通过 zoomDelta 控制步进
        this.map = L.map(this.containerId, {
            crs: FlippedSimpleCRS,
            minZoom: this.config.minZoom,
            maxZoom: this.config.maxZoom,
            zoomSnap: 0,
            zoomDelta: this.config.zoomDelta,
            attributionControl: false,
            zoomControl: false,
            // v3.29: pinch / 滚轮改由自写「合成式连续缩放引擎」接管（_bindSmoothZoom）。
            //   禁用 Leaflet 内置 touchZoom / scrollWheelZoom —— 它们走 _move → 每帧
            //   imageOverlay._reset() 重设 <img> width/height → 逐帧重栅格化背景图/边框（触屏卡顿主因）。
            //   引擎改为每帧 fire('zoomanim') 走各图层 _animateZoom 的 setTransform（GPU 合成、不重栅格）。
            touchZoom: false,
            scrollWheelZoom: false,
            // v3.36: 双击缩放也交给引擎（_zlDoubleClick），与 pinch / 滚轮同一套过渡
            doubleClickZoom: false,
            dragging: true,
            // v3.34: inertia:false —— 关掉惯性滑行(反向滑动"偏离"的主因;松手即停,无甩动 pan 动画)。
            inertia: false,
            bounceAtZoomLimits: false,   // v3.29: 去掉 min/max 处 pinch 回弹抖动
            // v3.35: maxBoundsViscosity 1.0→0 —— 让上下 / 左右两轴对称「自由拖出 + 松手弹回」。
            //   根因:硬边界(1.0)时,Leaflet Map.Drag._onDragStart 的粘滞限位 _offsetLimit 公式
            //   (latLngToContainerPoint(NW)*-1 / (SE)*-1+size) **假设 CRS 北在上**;而本仓自定义
            //   FlippedSimpleCRS(Y 向下, transformation c=1)让 NW/SE 的 y 投影翻转 → 纵向 _offsetLimit
            //   退化成超大对称范围 ≈ 不夹(纵向能自由拖出),横向不受翻转影响 → 被正确夹死(拖不出) →
            //   "上下能拖出、左右不能"的不对称。中间值治不了(纵向恒不受 viscosity 约束)。
            //   viscosity=0 → _onDragStart 令 _offsetLimit=null、_onPreDragLimit 直接 return,两轴
            //   都不夹 → 一致自由拖动;松手走 moveend→_panInsideMaxBounds 一并弹回(X 靠边 / Y 居中,
            //   该算法不受翻转影响)。回弹是 _panAnim,反向按下由 v3.33 pointerdown 停 pan 监听兜住不偏离。
            maxBoundsViscosity: 0
        });

        // 设置边界
        const bounds = [[0, 0], [this.config.height, this.config.width]];
        this.map.setMaxBounds(L.latLngBounds(bounds).pad(0.1));

        // v3.2: 不使用 fitBounds，直接设置视图到画布中心，zoom=0 原始尺寸
        const centerY = this.config.height / 2;
        const centerX = this.config.width / 2;
        const initialZoom = this.config.defaultZoom;
        this.map.setView([centerY, centerX], initialZoom);

        // 2026-10-18: 两个自定义 pane（都是 mapPane 的子层，随 v3.36 缩放引擎与 Leaflet 动画一起变换）：
        //   arkDecor(450) 放布景 —— 在底纹 / 背景图（overlayPane 400）之上、桌台（markerPane 600）之下；
        //   arkAbove(455) 放编辑网格 / 边线 / 对齐辅助线 —— 不被区域、墙盖住。pane 本身不吃指针，布景编辑模式只开条目自身。
        this._ensurePane('arkDecor', 450);
        this._ensurePane('arkAbove', 455);

        this._bindMapEvents();

        // v3.29: 自写「合成式连续缩放引擎」（pinch + 滚轮），取代被禁用的内置 touchZoom/scrollWheelZoom
        this._bindSmoothZoom();

        // v3.33: 惯性 pan 动画进行中按下 → 立即停住，让 Leaflet 拖动基准永远新鲜（修反向滑动"偏离"）。
        // 根因：Leaflet Draggable._onDown 按下时采样
        //   _startPos = getPosition(_mapPane) 作拖动基准，但它只防 leaflet-zoom-anim、不防 pan
        //   惯性动画（PosAnimation）—— 惯性未停时按下 → 采到动画中途的陈旧基准 → 手指一动 pane
        //   从动画终点跳回按下点 = 偏离。必须在 _onDown 采样前把 _panAnim stop 掉。
        // ⚠ 2026-08-28 更正：修法保留，但原注释的归因是错的 —— 原文称「Leaflet 经 DomEvent.Pointer
        //   把 Draggable 的 touchstart 转译成 container 上的 pointerdown，拖动实际由 pointerdown
        //   驱动」。该转译在 Leaflet 1.9.4 里被门控挡住了：DomEvent.on 的分支条件是
        //   `!Browser.touchNative && Browser.pointer && type.indexOf('touch') === 0`
        //   （leaflet-src.js:2804），而 `touchNative = 'ontouchstart' in window || !!window.TouchEvent`
        //   （:2019）在 Chromium / WebView2 下恒为 true → 走 else 分支，**绑的是真 touchstart**；
        //   Draggable 的 START 也确实是 `Browser.touch ? 'touchstart mousedown' : 'mousedown'`
        //   （:5968），挂在 container 的 bubble 阶段。所以「事件类型 touchstart ≠ pointerdown」
        //   不构成 v3.31/v3.32 失效的解释；**它们为何未生效目前未确证**，别当已知真相往下推。
        // v3.33 修法（仍然正确，理由比原文更简单）：Chromium 的按下序是 pointerdown → touchstart
        //   → mousedown，再叠加 document 的 capture 阶段严格早于 container 的 bubble 阶段 ——
        //   两重意义上都早于 Draggable._onDown 采样，**与注册先后无关**，保证先 stop 再采样。
        //   touchstart/mousedown 作非 pointer 环境兜底。
        //   _container.contains(e.target) 限定只处理本实例容器内的按下（多实例/全局安全）。
        // v3.36: 画布外按下（导航栏、楼层页签等）先补发尚未发出的视图通知 —— 接下来的点击可能换页 / 切楼层，
        //   通知必须赶在它前面到 .NET（JS → .NET 消息按发送顺序处理），否则视图记忆丢掉最后一次手势。
        this._onPointerDownStopPan = (e) => {
            if (!this._container) return;
            if (!this._container.contains(e.target)) { this._flushViewNotify(); return; }
            if (this.map && this.map._panAnim && this.map._panAnim._inProgress) {
                this.map._stop();
            }
        };
        document.addEventListener('pointerdown', this._onPointerDownStopPan, { capture: true, passive: true });
        document.addEventListener('touchstart', this._onPointerDownStopPan, { capture: true, passive: true });
        document.addEventListener('mousedown', this._onPointerDownStopPan, { capture: true, passive: true });

        // 画布底纹 / 地面素材最先建、永远最底（2026-10-18 起只建 config.backdrop 这一组，档位由宿主算好再传）
        this._createBackdrop();

        // v3.10: 网格在底层，先于 boundary 添加，确保 boundary/corner 在网格之上
        if (this.config.showGrid) {
            this._createGrid();
        }

        if (this.config.showBoundary) {
            this._createBoundary();
        }

        // 2026-10-18: 布景层（区域 + 物件），没有条目也建一个空层，setDecor 只换内容
        this._createDecor();

        if (this.config.editable) {
            container.classList.add('editable');
            // v3.23: 画布边界拖动 resize handle（仅编辑模式）
            this._createResizeHandles();
        }

        console.log('[ArkFloorPlan] Initialized:', {
            containerId: this.containerId,
            width: this.config.width,
            height: this.config.height,
            zoom: initialZoom,
            center: [centerX, centerY]
        });

        // v3.7: 修复首次 init 时容器尺寸未稳定导致 marker 错位 / 台号不显示
        // 1) 下一帧强制 invalidateSize：覆盖 init 与 AddTables 之间布局尚未稳定的 race
        // 2) ResizeObserver 监听容器后续尺寸变化（侧栏折叠 / 窗口 resize / 平板横竖屏切换等）
        // 3) v3.8: 首次拿到真实尺寸时额外 setView 回 center，避免 0×0 init 时视图锚错位置导致 marker 落在视口外
        this._needsInitialCenter = true;
        this._scheduleInvalidate();
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this._scheduleInvalidate());
            this._resizeObserver.observe(container);
        }

        // v3.16: 监听 Alt 键以支持 snap-to-grid 临时禁用
        this._altPressed = false;
        this._onKeyDown = (e) => { if (e.key === 'Alt' || e.altKey) this._altPressed = true; };
        this._onKeyUp = (e) => { if (e.key === 'Alt' || !e.altKey) this._altPressed = false; };
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('keyup', this._onKeyUp);

        return this;
    }

    /**
     * v3.7: 在下一帧让 Leaflet 重新读取容器尺寸；若未变化 invalidateSize 自身是廉价的
     * v3.8: 首次拿到真实容器尺寸时同步 setView 回 center，纠正 init 在 0×0 容器下锚定的视图原点
     * v3.9: 调换 setView / invalidateSize 顺序 —— 先设视图原点再让 Leaflet 重测尺寸，避免首帧两次 layer 重定位的视觉跳动
     */
    _scheduleInvalidate() {
        // v3.36: 缩放引擎未落地时不能动 Leaflet 的视图（invalidateSize 会平移 mapPane、改写图层坐标），落地后补做
        if (this._zl?.live) { this._zl.invalidate = true; return; }
        if (this._invalidatePending) return;
        this._invalidatePending = true;
        requestAnimationFrame(() => {
            this._invalidatePending = false;
            if (!this.map) return;

            const c = this._container;
            const hasSize = c && c.clientWidth > 0 && c.clientHeight > 0;

            if (hasSize && this._needsInitialCenter) {
                this._needsInitialCenter = false;
                const centerY = this.config.height / 2;
                const centerX = this.config.width / 2;
                this.map.setView([centerY, centerX], this.config.defaultZoom, { animate: false });
            }
            this.map.invalidateSize({ pan: false });

            // v3.9: 首帧把 --scale 同步到所有 marker
            this._applyScaleToAllMarkers();
            // v3.23: 首帧把 resize handles 投影到正确位置
            this._repositionHandles();
        });
    }

    /**
     * 重新初始化（使用新配置）
     */
    reinit(newOptions = {}) {
        const oldDotnetRef = this.dotnetRef;
        this.destroy();
        this.config = { ...DEFAULTS, ...newOptions };
        this.init(oldDotnetRef);

        console.log('[ArkFloorPlan] Reinitialized with new config:', {
            width: this.config.width,
            height: this.config.height
        });

        return this;
    }

    destroy() {
        this._clearAlignmentGuides();
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        // v3.16: 清理 Alt 键监听
        if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
        if (this._onKeyUp) window.removeEventListener('keyup', this._onKeyUp);
        this._onKeyDown = null;
        this._onKeyUp = null;
        // v3.23: 清理画布 resize handles
        this._removeResizeHandles();
        // v3.33: 解绑「按下停惯性」监听（document capture：pointerdown + touchstart/mousedown 兜底）
        if (this._onPointerDownStopPan) {
            document.removeEventListener('pointerdown', this._onPointerDownStopPan, { capture: true });
            document.removeEventListener('touchstart', this._onPointerDownStopPan, { capture: true });
            document.removeEventListener('mousedown', this._onPointerDownStopPan, { capture: true });
        }
        this._onPointerDownStopPan = null;
        // v3.29: 解绑合成缩放引擎（touch/wheel 监听 + rAF + 定时器）—— 须在 _container 置空前
        this._unbindSmoothZoom();
        // 2026-10-18: 布景编辑模块的监听；框选监听原先从不移除，reinit 一次就多挂一份
        if (this._decorEdit) this._decorEdit.dispose();
        this._decorEdit = null;
        if (this._rubberbandMouseDown && this._container) {
            this._container.removeEventListener('mousedown', this._rubberbandMouseDown, { capture: true });
        }
        this._rubberbandMouseDown = null;
        this._container = null;
        this._invalidatePending = false;
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
        this.markers.clear();
        this.backgroundLayer = null;
        this.boundaryLayer = null;
        this.backdropLayer = null;
        this.decorLayer = null;
        this._decorSvg = null;
        this.boundaryLines = [];
        this.boundaryCorners = [];
        this.gridLayer = null;
        this.selectedTableId = null;
        this.selectedTableIds.clear();
        this._alignmentGuides = [];
    }

    // ========== 动态更新边界 ==========

    /**
     * v3.26: 加 keepView 参数（resize 拖动期间传 true）防止 setView 重置中心导致桌台屏幕位置偏移
     */
    updateBounds(newWidth, newHeight, keepView = false) {
        if (!this.map) {
            console.warn('[ArkFloorPlan] updateBounds: map not initialized');
            return;
        }
        this._zoomSettleNow();

        this.config.width = newWidth;
        this.config.height = newHeight;

        const bounds = [[0, 0], [newHeight, newWidth]];
        this.map.setMaxBounds(L.latLngBounds(bounds).pad(0.1));

        if (!keepView) {
            // 默认重置到新画布中心（dialog 一次性改尺寸场景）
            const centerY = newHeight / 2;
            const centerX = newWidth / 2;
            this.map.setView([centerY, centerX], this.map.getZoom(), { animate: false });
        }

        // 画布尺寸变了：底纹层按新尺寸重建（仍在最底）
        this._removeBackdrop();
        this._createBackdrop();

        // v3.10: 网格在 boundary 之前重建，保证图层顺序
        this._removeGrid();
        if (this.config.showGrid) {
            this._createGrid();
        }

        this._removeBoundary();
        if (this.config.showBoundary) {
            this._createBoundary();
        }

        // 2026-10-18: 布景层只改视盒与边界，不重建（条目坐标与画布尺寸无关）
        this._resizeDecor(newWidth, newHeight);

        // v3.9: base iconSize 由桌台数据驱动，无需重建图标；仅同步视觉状态 + scale
        this.markers.forEach((marker) => this._applyVisualState(marker));
        this._applyScaleToAllMarkers();

        const zoomPercent = this.getZoomPercentage();
        this._notify('OnZoomChanged', { zoom: this.map.getZoom(), zoomPercent });

        // v3.23: 边界变化后同步 resize handle 屏幕位置
        this._repositionHandles();

        console.log('[ArkFloorPlan] Bounds updated successfully');
    }

    _removeBoundary() {
        // v3.27.20: 单一 svgOverlay，移除单层即可（兼容旧字段也清空）
        if (this.boundaryLayer) {
            if (this.map) this.map.removeLayer(this.boundaryLayer);
            this.boundaryLayer = null;
        }
        this.boundaryLines = [];
        this.boundaryCorners = [];
    }

    // ========== 画布底纹 / 地面素材（2026-10-04 门店级五档；2026-10-18 素材库 + 按楼层覆盖） ==========

    /**
     * 按 config.backdrop（档位名，C# FloorPlanBackdropResolver 算好再传）只建这一组：一个铺满画布的 svgOverlay。
     * 图形与随机种子在 ark-floor-plan-materials.js，颜色在 ark-floor-plan.css「地面素材」段（按 [data-mt] 给 CSS 变量）。
     * 用哪一档由宿主决定（POS = 楼层覆盖 ?? 门店默认；Admin 编辑器 = 只有楼层显式选的），JS 不读设置；
     * 有背景图时 CSS 据 .has-bg 隐藏（setBackground 维护，兜底 —— 宿主绑图时本就传 none）。
     * 永远压在所有图层最底（_sinkBackdrop）：其他图层 bringToBack 之后都要再沉一次。
     */
    _createBackdrop() {
        if (!this.map) return;
        const w = this.config.width;
        const h = this.config.height;
        const svg = buildBackdropSvg(this.config.backdrop, w, h, this.containerId);
        if (!svg) return;
        this.backdropLayer = L.svgOverlay(svg, [[0, 0], [h, w]], { interactive: false });
        this.backdropLayer.addTo(this.map);
        this.backdropLayer.bringToBack();
    }

    _removeBackdrop() {
        if (this.backdropLayer) {
            if (this.map) this.map.removeLayer(this.backdropLayer);
            this.backdropLayer = null;
        }
    }

    /** 2026-10-18: 换档位（参数变化路径；换楼层走 switchFloor，在淡出期间换） */
    setBackdrop(token) {
        const next = token || 'none';
        if (next === (this.config.backdrop || 'none')) return;
        this.config.backdrop = next;
        this._removeBackdrop();
        this._createBackdrop();
    }

    // ========== 布景（2026-10-18）：区域 + 物件，图形在 ark-floor-plan-decor.js ==========

    _ensurePane(name, zIndex) {
        if (!this.map || this.map.getPane(name)) return;
        const pane = this.map.createPane(name);
        pane.style.zIndex = String(zIndex);
        pane.style.pointerEvents = 'none';
    }

    _createDecor() {
        if (!this.map) return;
        const w = this.config.width;
        const h = this.config.height;
        this._decorSvg = createDecorSvg(w, h);
        renderDecor(this._decorSvg, this.config.decor, this.containerId);
        this.decorLayer = L.svgOverlay(this._decorSvg, [[0, 0], [h, w]], { interactive: false, pane: 'arkDecor' });
        this.decorLayer.addTo(this.map);
    }

    /** 换整份布景条目（换楼层 / 参数变化 / 撤销重做）。布景编辑模块拖动中会先挂起，拖完再应用 */
    setDecor(items) {
        this.config.decor = Array.isArray(items) ? items : [];
        if (this._decorEdit && this._decorEdit.deferSetDecor(this.config.decor)) return;
        if (!this._decorSvg) return;
        renderDecor(this._decorSvg, this.config.decor, this.containerId);
        if (this._decorEdit) this._decorEdit.onReplaced();
    }

    _resizeDecor(w, h) {
        if (!this._decorSvg || !this.decorLayer) return;
        this._decorSvg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        this._decorSvg.setAttribute('width', String(w));
        this._decorSvg.setAttribute('height', String(h));
        this.decorLayer.setBounds(L.latLngBounds([[0, 0], [h, w]]));
    }

    /** 底纹永远最底：背景图 / 网格 / 边线 bringToBack 之后都要再沉一次 */
    _sinkBackdrop() {
        if (this.backdropLayer) this.backdropLayer.bringToBack();
    }

    // ========== v3.10: 画布对齐网格（点阵）==========

    /**
     * 用 SVG svgOverlay 在画布坐标系内画 pattern，缩放时跟随地图变换；
     * 仅 config.showGrid=true 时调用，editable 视图通常打开
     */
    _createGrid() {
        if (!this.map) return;

        const w = this.config.width;
        const h = this.config.height;
        // v3.27.10: 双层网格 —— 小 4px 做底纹、大 80px 做主参考；密集铺满 = 画布边界自然显现
        const smallStep = 4;
        const bigStep = Math.max(smallStep, this.config.gridSize | 0);

        const svgNS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        svg.setAttribute('width', String(w));
        svg.setAttribute('height', String(h));
        svg.classList.add('ark-floor-plan-grid');

        const defs = document.createElementNS(svgNS, 'defs');
        const rand = Math.random().toString(36).slice(2, 8);

        // --- 小网格 pattern（4px）---
        const smallId = `ark-grid-s-${rand}`;
        const smallP = document.createElementNS(svgNS, 'pattern');
        smallP.setAttribute('id', smallId);
        smallP.setAttribute('width', String(smallStep));
        smallP.setAttribute('height', String(smallStep));
        smallP.setAttribute('patternUnits', 'userSpaceOnUse');
        const smallPath = document.createElementNS(svgNS, 'path');
        smallPath.setAttribute('d', `M ${smallStep} 0 L 0 0 0 ${smallStep}`);
        smallPath.setAttribute('fill', 'none');
        smallPath.classList.add('ark-floor-plan-grid-line-small');
        smallP.appendChild(smallPath);
        defs.appendChild(smallP);

        // --- 大网格 pattern（80px，嵌套填充小网格 + 叠加大网格线）---
        const bigId = `ark-grid-b-${rand}`;
        const bigP = document.createElementNS(svgNS, 'pattern');
        bigP.setAttribute('id', bigId);
        bigP.setAttribute('width', String(bigStep));
        bigP.setAttribute('height', String(bigStep));
        bigP.setAttribute('patternUnits', 'userSpaceOnUse');
        // 大格子内先以小网格填充
        const bigBg = document.createElementNS(svgNS, 'rect');
        bigBg.setAttribute('width', String(bigStep));
        bigBg.setAttribute('height', String(bigStep));
        bigBg.setAttribute('fill', `url(#${smallId})`);
        bigP.appendChild(bigBg);
        // 大网格线
        const bigPath = document.createElementNS(svgNS, 'path');
        bigPath.setAttribute('d', `M ${bigStep} 0 L 0 0 0 ${bigStep}`);
        bigPath.setAttribute('fill', 'none');
        bigPath.classList.add('ark-floor-plan-grid-line-big');
        bigP.appendChild(bigPath);
        defs.appendChild(bigP);

        svg.appendChild(defs);

        const rect = document.createElementNS(svgNS, 'rect');
        rect.setAttribute('width', '100%');
        rect.setAttribute('height', '100%');
        rect.setAttribute('fill', `url(#${bigId})`);
        svg.appendChild(rect);

        const bounds = [[0, 0], [h, w]];
        this.gridLayer = L.svgOverlay(svg, bounds, { interactive: false, pane: 'arkAbove' });
        this.gridLayer.addTo(this.map);
        this.gridLayer.bringToBack();
        // 背景图（如有）保持在网格之下；底纹永远最底
        if (this.backgroundLayer) this.backgroundLayer.bringToBack();
        this._sinkBackdrop();
    }

    _removeGrid() {
        if (this.gridLayer && this.map) {
            this.map.removeLayer(this.gridLayer);
            this.gridLayer = null;
        }
    }

    // ========== v3.23: 画布边界拖动 resize ==========
    // v3.24: 取消 handle DOM，改为容器 mousemove 检测鼠标 vs 边缘距离
    // 鼠标接近右边/下边/右下角（HIT_PX 范围内）→ cursor 切换 + mousedown 启动 resize

    /**
     * 挂 mousemove + mousedown 监听到容器，检测鼠标 vs 画布边界距离
     * 命名沿用 _createResizeHandles 兼容现有调用点（init / setEditable）
     * v3.27.23: 锁定画布尺寸时（绑定背景图）整套 mousemove + cursor 切换 + mousedown 全跳过
     */
    _createResizeHandles() {
        if (!this._container || !this.map) return;
        if (this.config.lockCanvasSize) return;
        this._removeResizeHandles();

        const container = this._container;
        const HIT_PX = 8;
        this._resizeHover = null;

        this._onResizeContainerMove = (e) => {
            if (this._resizing) return; // 拖动中不重新检测 hover
            // 2026-10-18: 布景编辑模式下不拉画布（边缘 8px 的按下要留给布景拖动）
            if (this._decorEditing) {
                if (this._resizeHover) { this._resizeHover = null; container.style.cursor = ''; }
                return;
            }
            const rect = container.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            // v3.26: CRS Y 翻转后 [H, W] = 屏幕右下，[0, 0] = 屏幕左上（与直觉一致）
            const screenBR = this.map.latLngToContainerPoint([this.config.height, this.config.width]);
            const screenTL = this.map.latLngToContainerPoint([0, 0]);

            const rightX = screenBR.x;
            const bottomY = screenBR.y;
            const leftX = screenTL.x;
            const topY = screenTL.y;

            const onRightLine = Math.abs(mx - rightX) <= HIT_PX && my >= topY && my <= bottomY;
            const onBottomLine = Math.abs(my - bottomY) <= HIT_PX && mx >= leftX && mx <= rightX;
            const nearBR = Math.abs(mx - rightX) <= HIT_PX && Math.abs(my - bottomY) <= HIT_PX;

            let hover = null;
            if (nearBR) hover = 'br';
            else if (onRightLine) hover = 'r';
            else if (onBottomLine) hover = 'b';

            if (hover !== this._resizeHover) {
                this._resizeHover = hover;
                container.style.cursor = hover === 'br' ? 'nwse-resize'
                    : hover === 'r' ? 'ew-resize'
                    : hover === 'b' ? 'ns-resize'
                    : '';
            }
        };

        this._onResizeContainerDown = (e) => {
            if (e.button !== 0 || !this._resizeHover || this._decorEditing) return;
            this._onResizeStart(e, this._resizeHover);
        };

        container.addEventListener('mousemove', this._onResizeContainerMove);
        container.addEventListener('mousedown', this._onResizeContainerDown, { capture: true });
    }

    _removeResizeHandles() {
        if (this._container) {
            if (this._onResizeContainerMove)
                this._container.removeEventListener('mousemove', this._onResizeContainerMove);
            if (this._onResizeContainerDown)
                this._container.removeEventListener('mousedown', this._onResizeContainerDown, { capture: true });
            this._container.style.cursor = '';
        }
        this._onResizeContainerMove = null;
        this._onResizeContainerDown = null;
        this._resizeHover = null;
        this._hideResizeBadge();
    }

    /** v3.24: 无 handle DOM，无需 reposition */
    _repositionHandles() { /* noop */ }

    _onResizeStart(e, handleType) {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        this._resizing = { type: handleType };
        // 高亮当前 handle
        const handle = this._resizeHandles?.[handleType];
        if (handle) handle.style.opacity = '1';

        this._onResizeMoveBound = (ev) => this._onResizeMove(ev);
        this._onResizeEndBound = (ev) => this._onResizeEnd(ev);
        document.addEventListener('mousemove', this._onResizeMoveBound);
        document.addEventListener('mouseup', this._onResizeEndBound);

        this._showResizeBadge();
        this._updateResizeBadge(this.config.width, this.config.height);
    }

    _onResizeMove(e) {
        if (!this._resizing || !this._container) return;
        const rect = this._container.getBoundingClientRect();
        // v3.26: CRS Y 翻转后 point.lat = 屏幕 Y 距离 = 画布"高度方向"距离
        //   point.lng = 屏幕 X 距离 = 画布"宽度方向"距离
        const point = this.map.containerPointToLatLng(L.point(e.clientX - rect.left, e.clientY - rect.top));

        const minW = this._minResizeW();
        const minH = this._minResizeH();
        const maxW = 4000;
        const maxH = 3000;

        let newW = this.config.width;
        let newH = this.config.height;
        if (this._resizing.type === 'br' || this._resizing.type === 'r') {
            newW = Math.max(minW, Math.min(maxW, point.lng));
        }
        if (this._resizing.type === 'br' || this._resizing.type === 'b') {
            newH = Math.max(minH, Math.min(maxH, point.lat));
        }

        // v3.16: snap-to-grid 复用（按 Alt 临时禁用）
        if (this.config.snapToGrid && !this._altPressed) {
            const g = this.config.gridSize || 80;
            if (this._resizing.type === 'br' || this._resizing.type === 'r') {
                newW = Math.max(minW, Math.round(newW / g) * g);
            }
            if (this._resizing.type === 'br' || this._resizing.type === 'b') {
                newH = Math.max(minH, Math.round(newH / g) * g);
            }
        }

        // v3.26: 仅当真有变化时调 updateBounds，且 keepView=true 防止 setView 重置中心
        if (newW !== this.config.width || newH !== this.config.height) {
            this.updateBounds(newW, newH, true);
        }
        this._updateResizeBadge(newW, newH);
    }

    _onResizeEnd() {
        document.removeEventListener('mousemove', this._onResizeMoveBound);
        document.removeEventListener('mouseup', this._onResizeEndBound);
        this._onResizeMoveBound = null;
        this._onResizeEndBound = null;

        this._hideResizeBadge();

        // v3.24: cursor 重置（_onResizeContainerMove 下次 hover 时再决定）
        if (this._container) this._container.style.cursor = '';
        this._resizeHover = null;

        // 通知 .NET
        this._notify('OnFloorPlanResized', {
            width: this.config.width,
            height: this.config.height
        });
        this._resizing = null;
    }

    /** 最小宽度 = max(table.X + table.W/2 + 80, 布景右缘, 200)（2026-10-18 起布景外框也算，免得拉小后被裁掉） */
    _minResizeW() {
        let m = 200;
        this.markers.forEach(marker => {
            const d = marker._tableData;
            const right = (d.x ?? 0) + ((d.w ?? this.config.defaultTableW) / 2) + 80;
            if (right > m) m = right;
        });
        this._decorBoxes().forEach(b => { if (b.r > m) m = b.r; });
        return m;
    }
    _minResizeH() {
        let m = 200;
        this.markers.forEach(marker => {
            const d = marker._tableData;
            const bottom = (d.y ?? 0) + ((d.h ?? this.config.defaultTableH) / 2) + 80;
            if (bottom > m) m = bottom;
        });
        this._decorBoxes().forEach(b => { if (b.b > m) m = b.b; });
        return m;
    }

    /** 布景条目外框（与渲染一致：图标类不转，其余 90 / 270 宽高互换） */
    _decorBoxes() {
        const icon = new Set(['restroom', 'kitchen', 'cashier', 'entrance']);
        return (this.config.decor || []).map(it => {
            const swap = !icon.has(it.kind) && (it.rotation === 90 || it.rotation === 270);
            const bw = swap ? it.h : it.w;
            const bh = swap ? it.w : it.h;
            return { id: it.id, l: it.x - bw / 2, t: it.y - bh / 2, r: it.x + bw / 2, b: it.y + bh / 2 };
        });
    }

    _showResizeBadge() {
        if (!this._container) return;
        if (this._resizeBadgeEl) return;
        // v3.24: 浮标位置 = 信息栏（左上角 top-4 left-4）；视觉风格沿用信息栏（bg-surface/60 + rounded + text-xs + text-muted）
        // 拖动期间覆盖信息栏，释放后移除自然恢复
        const el = document.createElement('div');
        el.className = 'ark-floor-plan-resize-badge';
        el.style.cssText =
            'position:absolute;left:16px;top:16px;' +
            'padding:4px 10px;' +
            'background:color-mix(in srgb, var(--color-surface, #fff) 60%, transparent);' +
            'border-radius:4px;' +
            'color:var(--color-foreground, #111);' +
            'font-size:12px;font-variant-numeric:tabular-nums;' +
            'backdrop-filter:blur(4px);' +
            'z-index:1001;pointer-events:none;user-select:none;';
        this._container.appendChild(el);
        this._resizeBadgeEl = el;
    }
    _updateResizeBadge(w, h) {
        if (!this._resizeBadgeEl) return;
        this._resizeBadgeEl.textContent = `${Math.round(w)} × ${Math.round(h)}`;
    }
    _hideResizeBadge() {
        if (!this._resizeBadgeEl) return;
        this._resizeBadgeEl.remove();
        this._resizeBadgeEl = null;
    }

    // ========== 缩放百分比计算 ==========

    /**
     * v3.2: 缩放百分比
     * zoom=0 时为 100%（原始尺寸）
     * zoom=1 时为 200%
     * zoom=-1 时为 50%
     */
    getZoomPercentage() {
        if (!this.map) return 100;
        return Math.round(Math.pow(2, this.getZoom()) * 100);
    }

    // ========== 楼层原地切换（v3.28）==========

    /**
     * v3.28: 一次性原子切换楼层 —— 替换尺寸 / 背景 / 桌台。
     *   性能特征:不销毁 L.map,不重建 Pane / ResizeObserver / 全局 keydown 监听 / .NET event 订阅。
     *
     * v3.28.2: 全程包在 100ms opacity fade 内 —— 把 setMaxBounds 的 viewport 自校正、
     *   桌台位置突变、setView 跳跃这 3 次视觉位移**全部藏在透明态**,用户只感受到淡入淡出,
     *   消除原本"晃动"感。原生 app crossDissolve 同款套路,GPU 合成不耗 CPU。
     *
     * options: {
     *   width, height,                          画布新尺寸
     *   backgroundUrl, backgroundWidth, ...,    背景图（替换式）
     *   backdrop                                2026-10-18: 新楼层的地面素材档位名
     *   decor                                   2026-10-18: 新楼层的布景条目
     *   tables, statusMap, extraDataMap,        桌台数据集
     *   zoom, centerX, centerY                  v3.28.2:还原视图（在 fade out 期间设,避免外层 setView 被看到）
     * }
     */
    async switchFloor(options = {}) {
        if (!this.map || !this._container) return;
        this._zoomSettleNow();

        const container = this._container;

        // Phase 1: fade out（CSS .ark-fp-switching：opacity 0，退场档 --ark-dur-exit）
        // 2026-10-27: 等待时长跟动效 token 走。原先写死 100ms —— 关闭动效时 opacity 瞬间到 0，平面图白等 100ms + 两帧才回来，
        //   换楼层闪一下空白。关闭动效时整段不隐藏：Phase 2 是同步执行完的，中途画不出半成品，直接一帧换成新楼层。
        const fadeOutMs = this._motionMs('--ark-dur-exit', 100);
        const fade = fadeOutMs > 0;
        if (fade) {
            container.classList.add('ark-fp-switching');
            await new Promise(r => setTimeout(r, fadeOutMs));
        }

        // Phase 2: 实际切换（瞬时 ~10ms;此时 opacity=0,所有位移用户不可见）
        const w = options.width ?? this.config.width;
        const h = options.height ?? this.config.height;
        const sizeChanged = (w !== this.config.width || h !== this.config.height);
        const hasViewRestore = (typeof options.zoom === 'number'
            && typeof options.centerX === 'number'
            && typeof options.centerY === 'number');

        // ★ v3.28.4: 把 setView 提前到 setMaxBounds 之前,避免 setMaxBounds 触发
        //   panInsideBounds 的 250ms pan 动画(原"楼层切换滑入"根因)。
        //   先 setMaxBounds(null) 解除旧限制,再 setView 跳到目标,最后 setMaxBounds(新);
        //   此时 viewport.center 已在新 bounds 内,setMaxBounds 不会触发 pan 动画。
        if (hasViewRestore) {
            this.map.setMaxBounds(null);
            this._needsInitialCenter = false;
            this.map.setView([options.centerY, options.centerX], options.zoom, { animate: false });
        }

        // 尺寸 / grid / boundary（始终重设 maxBounds,即便尺寸未变；center 已在新 bounds 内不触发 pan）
        this.config.width = w;
        this.config.height = h;
        const newMaxBounds = [[0, 0], [h, w]];
        this.map.setMaxBounds(L.latLngBounds(newMaxBounds).pad(0.1));

        // 2026-10-18: 新楼层的素材档位在淡出期间换（宿主在载荷里给；没给就沿用当前档位）
        const nextBackdrop = options.backdrop !== undefined ? (options.backdrop || 'none') : (this.config.backdrop || 'none');
        const backdropChanged = nextBackdrop !== (this.config.backdrop || 'none');
        this.config.backdrop = nextBackdrop;

        if (sizeChanged) {
            this._removeBackdrop();
            this._createBackdrop();
            this._removeGrid();
            if (this.config.showGrid) this._createGrid();
            this._removeBoundary();
            if (this.config.showBoundary) this._createBoundary();
            this._repositionHandles();
            this._resizeDecor(w, h);
        } else if (backdropChanged) {
            this._removeBackdrop();
            this._createBackdrop();
        }

        // 2026-10-18: 新楼层的布景（载荷没带就沿用 —— 只有旧调用方会这样）
        if (options.decor !== undefined) this.setDecor(options.decor);

        this.setBackground(options.backgroundUrl || '', {
            width: options.backgroundWidth ?? w,
            height: options.backgroundHeight ?? h,
            opacity: options.backgroundOpacity ?? 0.3
        });

        this.clearTables();
        if (options.tables && options.tables.length) {
            this.addTables(options.tables, options.statusMap || {}, options.extraDataMap || {});
        }

        if (!fade) return;

        // Phase 3: 等浏览器完成 layout + paint（双 rAF 让 marker DOM 落到位）
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

        // Phase 4: fade in
        container.classList.remove('ark-fp-switching');
    }

    /** 2026-10-27: 动效 token 的毫秒值；关闭动效（含系统减少动态效果）时 token 为 0 */
    _motionMs(token, fallback) {
        const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(token));
        return Number.isFinite(v) ? v : fallback;
    }

    // ========== 背景图 ==========

    setBackground(imageUrl, options = {}) {
        if (!this.map) return;

        // v3.28.1: 旧 layer 不立即 remove,先 fade out 再清理,与新 layer fade in 形成交叉淡入,
        //  消除楼层切换时的硬切漂移感（CSS .ark-floor-plan-bg-layer 提供 100ms opacity transition）
        const oldLayer = this.backgroundLayer;
        this.backgroundLayer = null;

        // 2026-10-04: 有背景图时 CSS 据 .has-bg 整层隐藏画布底纹（底纹只是无图时的兜底）
        if (this._container) this._container.classList.toggle('has-bg', !!imageUrl);

        if (!imageUrl) {
            if (oldLayer) this._fadeOutAndRemove(oldLayer);
            return;
        }

        const w = options.width || this.config.width;
        const h = options.height || this.config.height;
        const opacity = options.opacity ?? 0.3;

        const offsetX = (this.config.width - w) / 2;
        const offsetY = (this.config.height - h) / 2;
        const bounds = [[offsetY, offsetX], [offsetY + h, offsetX + w]];

        // 起始 opacity=0,addTo 后下一帧 setOpacity 触发 transition
        // 2026-10-27: instant = 首绘（容器还透明、整体揭幕时一起淡入）—— 直接到位，不再单独淡一次
        const instant = options.instant === true;
        this.backgroundLayer = L.imageOverlay(imageUrl, bounds, {
            opacity: instant ? opacity : 0,
            interactive: false,
            className: 'ark-floor-plan-bg-layer'
        }).addTo(this.map);

        this.backgroundLayer.bringToBack();
        this._sinkBackdrop();
        this._bringBoundaryToFront();

        if (instant) {
            if (oldLayer) this._fadeOutAndRemove(oldLayer);
            return;
        }

        const newLayer = this.backgroundLayer;
        requestAnimationFrame(() => {
            if (this.backgroundLayer === newLayer) {
                newLayer.setOpacity(opacity);
            }
            if (oldLayer) this._fadeOutAndRemove(oldLayer);
        });
    }

    /**
     * 2026-10-27: 首绘揭幕前等背景图解码完 —— 否则揭幕后图才到，又是一次跳变。
     * 有上限（默认 150ms = --ark-dur-base 的量级）：图慢就先揭幕，图到了自己出现，不让平面图一直透明。
     */
    async _awaitBackgroundDecoded(maxMs = 150) {
        const img = this.backgroundLayer?.getElement?.();
        if (!img || typeof img.decode !== 'function') return;
        if (img.complete && img.naturalWidth > 0) return;
        await Promise.race([
            img.decode().catch(() => { /* 图坏 / 被取消：照常揭幕 */ }),
            new Promise(r => setTimeout(r, maxMs))
        ]);
    }

    _fadeOutAndRemove(layer) {
        if (!layer) return;
        layer.setOpacity(0);
        // 与 CSS transition 时长（100ms）匹配；finally remove
        setTimeout(() => {
            if (this.map && this.map.hasLayer(layer)) {
                this.map.removeLayer(layer);
            }
        }, 120);
    }

    // ========== 桌台操作 ==========

    addTable(table, status = 0, extraData = {}) {
        if (!this.map) return null;

        const normalized = normalizeTable(table);
        if (!normalized.id) {
            console.warn('[ArkFloorPlan] addTable: missing table id');
            return null;
        }

        if (this.markers.has(normalized.id)) {
            this.removeTable(normalized.id);
        }

        const normalizedExtra = makeNormalizedExtra(extraData);
        const isLocked = getProp(normalizedExtra, 'locked', 'Locked') === true;

        // v3.9: 图标只输出常驻 DOM（不含状态属性），状态由 _applyVisualState 写入
        const icon = this._createIcon(normalized);
        const marker = L.marker([normalized.y, normalized.x], {
            icon,
            draggable: this.config.editable && !isLocked
        });

        marker._tableData = normalized;
        marker._status = status;
        marker._extraData = normalizedExtra;

        this._bindMarkerEvents(marker);
        marker.addTo(this.map);
        this.markers.set(normalized.id, marker);

        // marker DOM 已挂载，写入视觉状态；--scale 由容器继承,无需单 marker 写入
        this._applyVisualState(marker);

        return marker;
    }

    addTables(tables, statusMap = {}, extraDataMap = {}) {
        if (!this.map || !tables?.length) return;

        // v3.9: 去掉无引用的 L.layerGroup，避免 map._layers 累积空 group；直接挂到 map 与 addTable 一致
        // v3.11: 与 addTable 对齐 —— 同 id 已存在则先 removeTable，杜绝并发 RefreshTables 引发的游离 marker 累积
        const newMarkers = [];

        tables.forEach(table => {
            const normalized = normalizeTable(table);
            if (!normalized.id) return;

            // 防御：清掉同 id 的旧 marker（map 上的 layer + markers Map 引用）
            if (this.markers.has(normalized.id)) {
                this.removeTable(normalized.id);
            }

            const status = statusMap[normalized.id] ?? 0;
            const rawExtraData = extraDataMap[normalized.id] ?? {};
            const normalizedExtra = makeNormalizedExtra(rawExtraData);
            const isLocked = getProp(normalizedExtra, 'locked', 'Locked') === true;

            const icon = this._createIcon(normalized);
            const marker = L.marker([normalized.y, normalized.x], {
                icon,
                draggable: this.config.editable && !isLocked
            });

            marker._tableData = normalized;
            marker._status = status;
            marker._extraData = normalizedExtra;

            this._bindMarkerEvents(marker);
            marker.addTo(this.map);
            this.markers.set(normalized.id, marker);
            newMarkers.push(marker);
        });

        // 全部挂载完后统一刷视觉态 + 缩放
        newMarkers.forEach(m => this._applyVisualState(m));
        this._applyScaleToAllMarkers();
    }

    removeTable(tableId) {
        tableId = String(tableId);
        const marker = this.markers.get(tableId);
        if (marker) {
            this.map.removeLayer(marker);
            this.markers.delete(tableId);
            if (this.selectedTableId === tableId) {
                this.selectedTableId = null;
            }
            this.selectedTableIds.delete(tableId);
        }
    }

    clearTables() {
        this.markers.forEach(m => this.map.removeLayer(m));
        this.markers.clear();
    }

    updateTableStatus(tableId, status, extraData = {}) {
        tableId = String(tableId);
        const marker = this.markers.get(tableId);
        if (!marker) return;

        marker._status = status;
        // v3.9: 显式覆盖默认值，确保解锁等"消失态"也能被识别（JSON 可能省略 false）
        marker._extraData = makeNormalizedExtra(extraData);
        // 仅写属性 / 切显隐，无 DOM 重建
        this._applyVisualState(marker);
    }

    updateTableStatuses(updates) {
        if (!updates?.length) return;
        updates.forEach(u => {
            const tableId = String(getProp(u, 'tableId', 'TableId') ?? '');
            const status = getProp(u, 'status', 'Status') ?? 0;
            const extraData = getProp(u, 'extraData', 'ExtraData') ?? {};
            this.updateTableStatus(tableId, status, extraData);
        });
    }

    updateTablePosition(tableId, x, y) {
        tableId = String(tableId);
        const marker = this.markers.get(tableId);
        if (!marker) return;
        marker.setLatLng([y, x]);
        marker._tableData.x = x;
        marker._tableData.y = y;
        // v3.27.22: 位置直接更新（如抽屉编辑 W/H 后回写）→ 浮动工具栏同步
        if (this._floatingToolbar?.tableId === tableId) {
            this._repositionFloatingToolbar();
        }
    }

    updateTableShape(tableId, shape) {
        tableId = String(tableId);
        const marker = this.markers.get(tableId);
        if (!marker || !marker._tableData) return;

        const nextShape = Number(shape ?? 0);
        marker._tableData.shape = nextShape;

        const el = marker.getElement();
        const inner = el?.querySelector('.table-marker');
        if (inner) {
            inner.dataset.shape = String(nextShape);
            const currentShape = inner.querySelector('.table-shape');
            const w = marker._tableData.w || this.config.defaultTableW;
            const h = marker._tableData.h || this.config.defaultTableH;
            const nextShapeHtml = this._createShapeSvg(nextShape, w, h);

            if (currentShape) {
                currentShape.outerHTML = nextShapeHtml;
            } else {
                inner.insertAdjacentHTML('afterbegin', nextShapeHtml);
            }
        }

        this._applyVisualState(marker);
        if (this._floatingToolbar?.tableId === tableId) {
            this._repositionFloatingToolbar();
        }
    }

    // ========== 视图控制 ==========

    // v3.36: 缩放一律走引擎过渡（对数插值，--ark-dur-base），不再用 Leaflet 自带的 250ms CSS 动画
    setZoom(zoom) { if (this.map) this._zlZoomAround(this.map.getSize().divideBy(2), zoom); }
    zoomIn() { if (this.map) this._zlZoomAround(this.map.getSize().divideBy(2), this.getZoom() + this.map.options.zoomDelta); }
    zoomOut() { if (this.map) this._zlZoomAround(this.map.getSize().divideBy(2), this.getZoom() - this.map.options.zoomDelta); }
    /** v3.36: 引擎未落地时返回画面上的实际缩放（越界部分按回位后的值） */
    getZoom() {
        if (!this.map) return 0;
        return this._zl?.live ? this._zlClampZoom(this._zlZoom()) : this.map.getZoom();
    }

    /**
     * 一并读取 zoom + center,供调用方在切换楼层 / 切换平面图前快照,
     * 切回时调 setView 恢复。字段命名对齐 OnViewChanged 事件 payload
     * （ViewChangeEventArgs.CenterX/CenterY/Zoom）—— centerX = lng / centerY = lat。
     *
     * v3.36: 先让引擎落地，再丢弃尚未发出的视图通知 —— 调用方（TablePlanSelector 切楼层）自己记下这份视图；
     *   若放任防抖通知晚到，它会被记到已经切换过去的新楼层名下。
     */
    getView() {
        if (!this.map) return null;
        this._zoomSettleNow();
        this._cancelViewNotify();
        const c = this.map.getCenter();
        return { zoom: this.map.getZoom(), centerX: c.lng, centerY: c.lat };
    }

    /**
     * 一次性恢复 zoom + center,无动画（楼层切换瞬时回到记忆视图,避免肉眼可见的 fly-in）。
     * 外部主动 setView 后取消 _needsInitialCenter,避免 _scheduleInvalidate 的 rAF 回调
     * 把视图覆盖回 defaultZoom（reinit 后切楼层 viewport 重置的核心 bug）。
     */
    setView(zoom, centerX, centerY) {
        if (!this.map) return;
        this._zoomSettleNow();
        this._needsInitialCenter = false;
        this.map.setView([centerY, centerX], zoom, { animate: false });
    }

    /**
     * 重置视图到画布中心，zoom=0
     */
    resetView() {
        if (!this.map) return;
        this._zlViewTo(L.latLng(this.config.height / 2, this.config.width / 2), 0);
    }

    /**
     * 适应画布到容器（可选功能）
     */
    fitToContainer() {
        if (!this.map) return;
        const bounds = L.latLngBounds([[0, 0], [this.config.height, this.config.width]]);
        this._zlViewTo(bounds.getCenter(), this.map.getBoundsZoom(bounds));
    }

    focusTable(tableId, zoom) {
        tableId = String(tableId);
        const marker = this.markers.get(tableId);
        if (!marker || !this.map) return;
        this._zlViewTo(marker.getLatLng(), zoom ?? this.getZoom());
    }

    // ========== 编辑模式 ==========

    setEditable(editable) {
        this.config.editable = editable;
        const container = document.getElementById(this.containerId);

        if (editable) {
            container?.classList.add('editable');
            // v3.23: 进入编辑模式时创建 resize handles（v3.27.23: lockCanvasSize 时 _createResizeHandles 内部直接 return）
            if (!this._resizeHandles) this._createResizeHandles();
        } else {
            container?.classList.remove('editable');
            // v3.23: 退出编辑模式时移除 resize handles
            this._removeResizeHandles();
        }

        this.markers.forEach(marker => {
            if (editable && !marker._extraData?.locked) {
                marker.dragging?.enable();
            } else {
                marker.dragging?.disable();
            }
        });
    }

    toggleBoundary(show) {
        this.config.showBoundary = show;
        if (show && !this.boundaryLayer) {
            this._createBoundary();
        } else if (!show && this.boundaryLayer) {
            this._removeBoundary();
        }
    }

    /**
     * v3.15: 单选向后兼容入口 —— 内部转 setSelectedTables([id]) / setSelectedTables([])
     * 调用方继续可用 selectTableAsync(id) / selectTableAsync(null)
     */
    selectTable(tableId) {
        if (tableId === null || tableId === undefined) {
            this.setSelectedTables([]);
        } else {
            this.setSelectedTables([String(tableId)]);
        }
    }

    /**
     * v3.18: 持续多选模式开关（工具栏 toggle）
     * 开启时：拖空白 = 框选；点 marker = toggle；不需要按 Ctrl
     */
    setPersistentMultiMode(enabled) {
        this._persistentMultiMode = !!enabled;
    }

    /**
     * v3.15: 设置多选集合（替换语义）
     * idArray 为空 → 清空选中
     * 第一个 id 作为"主选中"selectedTableId（兼容单选 UI / drawer）
     */
    setSelectedTables(idArray) {
        const newSet = new Set((idArray ?? []).map(x => String(x)));
        const prevSet = this.selectedTableIds;

        // 收集需要刷视觉的 marker（旧/新差集）
        const toRefresh = new Set();
        for (const id of prevSet) if (!newSet.has(id)) toRefresh.add(id);
        for (const id of newSet) if (!prevSet.has(id)) toRefresh.add(id);

        this.selectedTableIds = newSet;
        this.selectedTableId = idArray && idArray.length > 0 ? String(idArray[idArray.length - 1]) : null;

        toRefresh.forEach(id => {
            const m = this.markers.get(id);
            if (m) this._applyVisualState(m);
        });
    }

    // ========== 碰撞检测 ==========

    /**
     * 获取碰撞检测盒（直接使用画布坐标）
     * v3.12: 携带 shape 字段，配合 _checkCollision 实现形状感知
     *   shape: 0=矩形, 1=圆形/椭圆, 2=菱形（继续用 AABB 近似）
     */
    _getCollisionBox(tableData) {
        const padding = this.config.collisionPadding;
        const w = tableData.w + padding * 2;
        const h = tableData.h + padding * 2;

        return {
            x: tableData.x,
            y: tableData.y,
            halfW: w / 2,
            halfH: h / 2,
            shape: tableData.shape ?? 0
        };
    }

    /**
     * v3.12: 形状感知碰撞
     * - 圆-圆：圆心距 < 半径和（椭圆退化为长短半轴平均的等效圆）
     * - 圆-矩形：圆心到矩形最近点距离 < 圆半径
     * - 其他（矩形-矩形 / 含菱形）：保持 AABB（菱形按外接矩形近似，与 v3.11 之前一致）
     */
    _checkCollision(box1, box2) {
        const isCircle1 = box1.shape === 1;
        const isCircle2 = box2.shape === 1;

        // 双圆
        if (isCircle1 && isCircle2) {
            const r1 = (box1.halfW + box1.halfH) / 2;
            const r2 = (box2.halfW + box2.halfH) / 2;
            const dx = box1.x - box2.x;
            const dy = box1.y - box2.y;
            return (dx * dx + dy * dy) < (r1 + r2) * (r1 + r2);
        }

        // 圆-矩形
        if (isCircle1 || isCircle2) {
            const c = isCircle1 ? box1 : box2;
            const r = isCircle1 ? box2 : box1;
            const cr = (c.halfW + c.halfH) / 2;
            const closestX = Math.max(r.x - r.halfW, Math.min(c.x, r.x + r.halfW));
            const closestY = Math.max(r.y - r.halfH, Math.min(c.y, r.y + r.halfH));
            const dx = c.x - closestX;
            const dy = c.y - closestY;
            return (dx * dx + dy * dy) < cr * cr;
        }

        // 矩形 / 含菱形：AABB
        return Math.abs(box1.x - box2.x) < (box1.halfW + box2.halfW)
            && Math.abs(box1.y - box2.y) < (box1.halfH + box2.halfH);
    }

    _hasCollisionWith(tableId, x, y, tableData) {
        if (!this.config.enableCollision) return false;

        const testBox = this._getCollisionBox({ ...tableData, x, y });

        for (const [id, marker] of this.markers) {
            if (id === tableId) continue;
            const otherBox = this._getCollisionBox(marker._tableData);
            if (this._checkCollision(testBox, otherBox)) {
                return true;
            }
        }
        return false;
    }

    /**
     * v3.27: 与 _hasCollisionWith 一致，但跳过当前 _dragGroupOrigin 内的成员
     * （组内成员之间不互相检测——它们 drag 中位移完全一致，相对位置不变）
     */
    _hasCollisionWithOutsideGroup(tableId, x, y, tableData) {
        if (!this.config.enableCollision) return false;

        const testBox = this._getCollisionBox({ ...tableData, x, y });
        const groupSet = this._dragGroupOrigin;

        for (const [id, marker] of this.markers) {
            if (id === tableId) continue;
            if (groupSet && groupSet.has(id)) continue;
            const otherBox = this._getCollisionBox(marker._tableData);
            if (this._checkCollision(testBox, otherBox)) {
                return true;
            }
        }
        return false;
    }

    /**
     * v3.27: 整组按 (dx, dy) 平移后，是否有任一成员撞到"组外"marker 或被画布边界裁剪
     * v3.27.3: 边界裁剪也算撞墙 —— 否则非 held 成员被画布约束默默拉回，组继续推进
     *   导致组内 marker 视觉堆叠（截图：T08 卡顶边、T011 滑过来重叠 T08）
     */
    _groupOffsetCollides(dx, dy) {
        if (!this._dragGroupOrigin) return false;
        for (const [id, origin] of this._dragGroupOrigin) {
            const m = this.markers.get(id);
            if (!m) continue;
            const proposedX = origin.x + dx;
            const proposedY = origin.y + dy;
            const c = this._constrainPosition(proposedX, proposedY, m._tableData);
            // 任一成员被画布边界拉回 → 撞墙
            if (c.x !== proposedX || c.y !== proposedY) {
                return true;
            }
            // 任一成员撞组外 marker → 撞墙
            if (this._hasCollisionWithOutsideGroup(id, c.x, c.y, m._tableData)) {
                return true;
            }
        }
        return false;
    }

    /**
     * v3.27: dragend 兜底——在 (dx, dy) 平面环式扩散搜索最近合法位移
     * 步长 8、8 方向、maxDist = 画布对角线一半。失败返回 null。
     */
    _findNearestSafeGroupOffset(dx, dy) {
        if (!this._groupOffsetCollides(dx, dy)) return { dx, dy };
        const step = 8;
        const maxDist = Math.hypot(this.config.width, this.config.height) / 2;
        const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
        for (let d = step; d <= maxDist; d += step) {
            for (const [ux, uy] of dirs) {
                const tdx = dx + ux * d;
                const tdy = dy + uy * d;
                if (!this._groupOffsetCollides(tdx, tdy)) {
                    return { dx: tdx, dy: tdy };
                }
            }
        }
        return null;
    }

    /**
     * v3.30: 二分逼近接触点 —— 在 from(安全)→to(碰撞) 线段上找能到达的最远安全位置。
     * 8 次迭代精度 = 位移/256（亚像素级）；每次一趟 _hasCollisionWith（O(N) 纯算术，无 DOM）。
     * 形状无关：统一走 _checkCollision 的形状感知逻辑，无需逐形状接触方程。
     * 返回点必为已实测安全的位置（lo 侧只在测试通过时推进）。
     */
    _clampToContact(tableId, fromX, fromY, toX, toY, tableData) {
        let lo = 0, hi = 1;
        for (let i = 0; i < 8; i++) {
            const mid = (lo + hi) / 2;
            const mx = fromX + (toX - fromX) * mid;
            const my = fromY + (toY - fromY) * mid;
            if (this._hasCollisionWith(tableId, mx, my, tableData)) hi = mid;
            else lo = mid;
        }
        return { x: fromX + (toX - fromX) * lo, y: fromY + (toY - fromY) * lo };
    }

    /**
     * v3.30: 硬墙"贴墙"解算 —— 先沿运动方向夹紧到接触点，再对残余位移做轴向滑墙（X 优先，
     * 与 v3.13"水平移动更常见"的取向一致）。取代旧的"回退 last 采样位置"：
     * 无论单帧鼠标位移多大，都停在允许的最近位置而不是上一帧的任意空隙处。
     * from 必须安全；若 from 本身碰撞（历史脏数据）直接原地返回（与旧"都堵不动"行为一致）。
     */
    _slideToContact(tableId, fromX, fromY, toX, toY, tableData) {
        if (this._hasCollisionWith(tableId, fromX, fromY, tableData)) {
            return { x: fromX, y: fromY };
        }
        let c = this._clampToContact(tableId, fromX, fromY, toX, toY, tableData);
        // 残余 X：固定 c.y，向 toX 推进（能全走就全走，否则夹紧到接触）
        if (!this._hasCollisionWith(tableId, toX, c.y, tableData)) {
            c = { x: toX, y: c.y };
        } else {
            c = this._clampToContact(tableId, c.x, c.y, toX, c.y, tableData);
        }
        // 残余 Y：固定 c.x，向 toY 推进
        if (!this._hasCollisionWith(tableId, c.x, toY, tableData)) {
            c = { x: c.x, y: toY };
        } else {
            c = this._clampToContact(tableId, c.x, c.y, c.x, toY, tableData);
        }
        return c;
    }

    /**
     * v3.30: 组级二分夹紧 —— 在 (fromDx,fromDy)→(toDx,toDy) 位移线段上找最远安全组位移。
     * 判定走 _groupOffsetCollides（含 v3.27.3"画布边界裁剪也算撞墙"判据）。
     */
    _clampGroupOffsetToContact(fromDx, fromDy, toDx, toDy) {
        let lo = 0, hi = 1;
        for (let i = 0; i < 8; i++) {
            const mid = (lo + hi) / 2;
            if (this._groupOffsetCollides(
                fromDx + (toDx - fromDx) * mid,
                fromDy + (toDy - fromDy) * mid)) hi = mid;
            else lo = mid;
        }
        return { dx: fromDx + (toDx - fromDx) * lo, dy: fromDy + (toDy - fromDy) * lo };
    }

    /**
     * v3.30: dragend 取整后的 1px 邻位修正 —— round 产生的亚像素重叠在 8 邻位内找安全整数点。
     * collides(x, y) 由调用方提供（单选=全量检测；组拖=仅组外检测）。
     * 出界候选跳过；找不到返回 null（调用方保持原值，与旧行为一致）。
     */
    _nudgeRoundedSafe(x, y, tableData, collides) {
        const nudges = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
        for (const [nx, ny] of nudges) {
            const tx = x + nx;
            const ty = y + ny;
            const c = this._constrainPosition(tx, ty, tableData);
            if (c.x !== tx || c.y !== ty) continue;
            if (!collides(tx, ty)) return { x: tx, y: ty };
        }
        return null;
    }

    checkCollisionAt(tableId, x, y, w, h) {
        const tableData = { x, y, w, h };
        return this._hasCollisionWith(String(tableId), x, y, tableData);
    }

    /**
     * ⚠ 一致性红线：本函数与 _getCollisionBox / _checkCollision / _constrainPosition 四者
     *   在 C# 侧有一份逐条对译 —— Ark.UI/Components/FloorPlan/FloorPlanLayout.cs。
     *   那份服务于「网格模式不渲染画布、拿不到 JS」的场景（手机端默认在网格模式）。
     *   同一张平面图上两边必须服从同一套判据，否则会出现「网格模式建的台，
     *   切到平面图模式就显示重叠」。**改这里必须同步改那里。**
     */
    findSafePosition(w, h, preferX, preferY) {
        const tableData = { w, h };
        const testId = '__new_table_test__';

        const constrained = this._constrainPosition(preferX, preferY, tableData);
        preferX = constrained.x;
        preferY = constrained.y;

        if (!this._hasCollisionWith(testId, preferX, preferY, tableData)) {
            return { x: preferX, y: preferY, hasCollision: false };
        }

        const step = Math.max(w, h) + this.config.collisionPadding * 2;
        // v3.12: 环数自适应到画布对角线，覆盖整张画布；最少 5 环
        const diag = Math.hypot(this.config.width, this.config.height);
        const maxRing = Math.max(5, Math.ceil(diag / step));

        for (let ring = 1; ring <= maxRing; ring++) {
            for (let i = 0; i < ring * 8; i++) {
                const angle = (i / (ring * 8)) * Math.PI * 2;
                const testX = preferX + Math.cos(angle) * step * ring;
                const testY = preferY + Math.sin(angle) * step * ring;

                const pos = this._constrainPosition(testX, testY, tableData);
                if (Math.abs(pos.x - testX) > 1 || Math.abs(pos.y - testY) > 1) continue;

                if (!this._hasCollisionWith(testId, pos.x, pos.y, tableData)) {
                    return { x: pos.x, y: pos.y, hasCollision: false };
                }
            }
        }

        return { x: preferX, y: preferY, hasCollision: true };
    }

    // ========== 私有方法 ==========

    /**
     * v3.9: 图标只输出常驻 DOM 骨架（不含状态），状态由 _applyVisualState 写入
     * iconSize 锁定为 base 尺寸，缩放仅写 CSS 变量 --scale，浏览器走 transition 平滑过渡
     * 桌台号 / data-id 走 escapeHtml 防注入
     *
     * ⚠ .table-btime 里那枚日历 SVG 与 Ark.UI/Assets/LucideIcons.cs 的 Icons.Calendar
     *   是**同一个字形的两份拷贝**（这一面没有 Blazor 组件可用，只能内联）。
     *   网格磁贴那面走 <ArkIcon Name="Icons.Calendar" />，改字形要两处同步，否则同一枚标记
     *   在地图与网格上长相不同 —— 这个域历史上已经靠"逐字同口径"硬维持一致过好几轮。
     */
    _createIcon(table) {
        const w = table.w || this.config.defaultTableW;
        const h = table.h || this.config.defaultTableH;
        const shape = table.shape ?? 0;

        const svgShape = this._createShapeSvg(shape, w, h);

        const safeId = escapeHtml(table.id);
        const safeCode = escapeHtml(table.code || '');
        // 部分支付货币图标：币种取自桌台容器的 data-paid-icon（与 data-map-booking 同一取法；门店币种会话内不变）
        const paidSvg = paidIconSvg(this._container?.closest?.('[data-paid-icon]')?.dataset.paidIcon);

        const html = `
            <div class="ark-table table-marker"
                 data-id="${safeId}" data-shape="${shape}"
                 data-status="0" data-locked="false"
                 data-locked-by-other="false" data-selected="false"
                 data-disabled="false" data-excluded="false"
                 data-booking-overdue="false"
                 style="width:${w}px;height:${h}px;--th:${h}px">
                ${svgShape}
                <div class="table-content">
                    <div class="table-code">${safeCode}</div>
                    <div class="table-extra" hidden><span class="gc"><svg class="gi" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/></svg><span class="gn"></span></span><span class="am"></span></div>
                    <div class="table-booking" hidden></div>
                    <div class="table-btime" hidden><svg class="bi" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v3"/><path d="M16 2v3"/><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg><span class="tm"></span><span class="mr"></span></div>
                    <div class="table-duration" hidden></div>
                </div>
                <div class="table-lock" hidden>
                    <svg class="bd-disc" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    ${HALO_LOCK_SVG}
                </div>
                <div class="table-qr-badge" hidden title="客人已扫码下单">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect width="5" height="5" x="3" y="3" rx="1"/>
                        <rect width="5" height="5" x="16" y="3" rx="1"/>
                        <rect width="5" height="5" x="3" y="16" rx="1"/>
                        <path d="M21 16h-3a2 2 0 0 0-2 2v3"/>
                        <path d="M21 21v.01"/>
                        <path d="M12 7v3a2 2 0 0 1-2 2H7"/>
                        <path d="M3 12h.01"/>
                        <path d="M12 3h.01"/>
                        <path d="M12 16v.01"/>
                        <path d="M16 12h1"/>
                        <path d="M21 12v.01"/>
                        <path d="M12 21v-1"/>
                    </svg>
                </div>
                <div class="table-paid" hidden>${paidSvg}</div>
                <div class="table-bcal" hidden>${HALO_CAL_SVG}</div>
                <div class="table-kitchen-done" hidden></div>
                <div class="table-kitchen-pending" hidden></div>
                <div class="table-bill-printed" hidden title="客人账单已打印">
                    <svg class="bd-disc" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round">
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                        <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"/>
                        <rect x="6" y="14" width="12" height="8" rx="1"/>
                    </svg>
                    ${HALO_PRINTER_SVG}
                </div>
            </div>`;

        return L.divIcon({
            html,
            className: 'ark-floor-plan-marker',
            iconSize: [w, h],
            iconAnchor: [w / 2, h / 2]
        });
    }

    /**
     * v3.9: 把 marker._status / _extraData / 选中态 派生为 DOM 属性 + 显隐
     * 不重建 DOM，仅写 dataset / hidden / textContent / title
     */
    _applyVisualState(marker) {
        const el = marker.getElement();
        if (!el) return;
        const inner = el.querySelector('.table-marker');
        if (!inner) return;

        const status = marker._status ?? 0;
        const extraData = marker._extraData ?? {};
        const tableId = marker._tableData?.id;

        const isLocked = getProp(extraData, 'locked', 'Locked') === true;
        const lockedByMe = getProp(extraData, 'lockedByMe', 'LockedByMe') === true;
        const isLockedByOther = isLocked && !lockedByMe;
        const isDisabled = getProp(extraData, 'disabled', 'Disabled') === true;
        const isExcluded = getProp(extraData, 'excluded', 'Excluded') === true;
        const hasOnlineOrder = getProp(extraData, 'hasOnlineOrder', 'HasOnlineOrder') === true;
        const isBookingOverdue = getProp(extraData, 'bookingOverdue', 'BookingOverdue') === true;
        const isSelected = tableId != null && this.selectedTableIds.has(String(tableId));

        inner.dataset.status = String(status);
        inner.dataset.locked = String(isLocked);
        inner.dataset.lockedByOther = String(isLockedByOther);
        inner.dataset.disabled = String(isDisabled);
        inner.dataset.excluded = String(isExcluded);
        inner.dataset.bookingOverdue = String(isBookingOverdue);
        inner.dataset.selected = String(isSelected);

        // 锁徽章显隐（仅真锁——有人正在操作此桌；筛选不符 disabled 走 grayscale 不显示角标，避免满屏锁眼花）
        const lockEl = inner.querySelector('.table-lock');
        if (lockEl) {
            if (isLocked && !isDisabled) {
                lockEl.removeAttribute('hidden');
            } else {
                lockEl.setAttribute('hidden', '');
            }
        }

        // QR 徽章显隐
        const qrEl = inner.querySelector('.table-qr-badge');
        if (qrEl) {
            if (hasOnlineOrder) qrEl.removeAttribute('hidden');
            else qrEl.setAttribute('hidden', '');
        }

        // 部分支付货币底层图标显隐。与扫码图标一样不看 disabled：并台选台时也要先看出哪桌付过钱
        const paidEl = inner.querySelector('.table-paid');
        if (paidEl) {
            if (getProp(extraData, 'hasPosPayment', 'HasPosPayment') === true) paidEl.removeAttribute('hidden');
            else paidEl.setAttribute('hidden', '');
        }

        // 上菜状态「出齐」徽章显隐（绿底白勾 Check）— 图标静态写在 _createIcon，此处仅显隐（右下角，与制作中互斥同槽）
        const allKitchenReady = getProp(extraData, 'allKitchenItemsReady', 'AllKitchenItemsReady') === true;
        const kitchenEl = inner.querySelector('.table-kitchen-done');
        if (kitchenEl) {
            if (allKitchenReady && !isDisabled) {
                kitchenEl.removeAttribute('hidden');
            } else {
                kitchenEl.setAttribute('hidden', '');
            }
        }

        // 上菜状态「制作中」徽章显隐（琥珀底白钟 Clock）— 与出齐业务互斥
        const hasKitchenPending = getProp(extraData, 'hasKitchenPending', 'HasKitchenPending') === true;
        const pendingEl = inner.querySelector('.table-kitchen-pending');
        if (pendingEl) {
            if (hasKitchenPending && !isDisabled) {
                pendingEl.removeAttribute('hidden');
            } else {
                pendingEl.setAttribute('hidden', '');
            }
        }

        // 客人账单（预结单）已打印徽章显隐（门控已在 C# 侧按 ShowBillPrintedBadge 完成，此处仅按值显隐）
        const billPrinted = getProp(extraData, 'billPrinted', 'BillPrinted') === true;
        const billEl = inner.querySelector('.table-bill-printed');
        if (billEl) {
            if (billPrinted && !isDisabled) billEl.removeAttribute('hidden');
            else billEl.setAttribute('hidden', '');
        }

        // 额外信息文本（textContent 自动转义）— 金额单独一行，人数 + 时长合一行
        const amount = getProp(extraData, 'amount', 'Amount');
        const duration = getProp(extraData, 'duration', 'Duration');
        const guestCount = getProp(extraData, 'guestCount', 'GuestCount');

        // 用时 —— 贴底居中（.table-duration；怎么贴由末尾写的 data-duration 决定）。
        // 空台 / 门店关用时时整行 hidden 且**不预留高度**：主体组回到整格居中。
        const contentEl = inner.querySelector('.table-content');
        const durationEl = inner.querySelector('.table-duration');
        const showDurationRow = status !== 0 && this.config.showDuration && !!duration;
        if (durationEl) {
            if (showDurationRow) {
                durationEl.textContent = String(duration);
                durationEl.removeAttribute('hidden');
            } else {
                durationEl.textContent = '';
                durationEl.setAttribute('hidden', '');
            }
        }

        // 预订信息 —— 四个字段独立于 duration，不再借道「用餐时长」门控。
        // bookingCount > 0 即有预订；bookingTimeText 恒为**一笔**（最早的活跃预订），
        // 其余笔数由同行的 `+N` 带过；bookingTone 是那一笔的状态色族名。
        const bookingCount = Number(getProp(extraData, 'bookingCount', 'BookingCount') ?? 0);
        const bookingTimeText = getProp(extraData, 'bookingTimeText', 'BookingTimeText');
        const bookingTone = getProp(extraData, 'bookingTone', 'BookingTone');
        const bookingName = getProp(extraData, 'bookingName', 'BookingName');
        const bookingPhoneTail = getProp(extraData, 'bookingPhoneTail', 'BookingPhoneTail');
        const hasBooking = this.config.showBookingBadge && bookingCount > 0;
        // 图标模式（桌台页 / 选桌弹窗）：只出左下角日历图标，时刻行与姓名行整体不出现；
        // 完整模式（预订页，预订时刻是那一页的主信息）：沿用下面两行文字，不出图标。
        const bookingIconOnly = !!this._container?.closest?.('[data-map-booking="icon"]');

        // 实况行 = [人数图标] 人数 + 金额，同一行、人数在前（与网格 tile 同序）。用时已单独贴底。
        // 预订从不并进本行（marker 内容框只有 74px）。金额恒 nowrap 不截断 —— 截断的金额是错的数字。
        const extraEl = inner.querySelector('.table-extra');
        if (extraEl) {
            const showGuests = status !== 0 && this.config.showGuestCount && !!guestCount;
            const showAmountPart = status !== 0 && this.config.showAmount && amount != null && amount !== '';
            const gcEl = extraEl.querySelector('.gc');
            const amEl = extraEl.querySelector('.am');
            extraEl.querySelector('.gn').textContent = showGuests ? String(guestCount) : '';
            amEl.textContent = showAmountPart ? `¥${amount}` : '';
            gcEl.hidden = !showGuests;
            amEl.hidden = !showAmountPart;
            if (showGuests || showAmountPart) extraEl.removeAttribute('hidden');
            else extraEl.setAttribute('hidden', '');
        }

        // 预订提示块第 ① 行：时刻（状态色）+ `+N`。
        //
        // 写 `+N` 而不是 `×N`：时刻已经摊开写了其中一笔，再写总数要读者自己做减法，
        // `×2` 还容易被读成「17:30 有两桌」。单笔时 `+N` 整个不出现，行数不变。
        //
        // data-tone 只写属性、色值展开在 ark-floor-plan.css（含「只有浅底磁贴才着色」那道门）；
        // 撤空要用 removeAttribute —— 写空串会让 [data-tone=""] 命中不到任何色族但属性仍在，
        // 将来加兜底选择器时会踩。
        //
        // 纵向上界（实测）：台号 19.2 + gap 1 + 三行（用时/时刻/姓名）33 = 53.2px，
        // marker 内容框 74px 放得下，菱形 56px 也留得住 2.8px —— 与收敛前持平。
        const btimeEl = inner.querySelector('.table-btime');
        if (btimeEl) {
            if (hasBooking && bookingTimeText && !bookingIconOnly) {
                btimeEl.querySelector('.tm').textContent = String(bookingTimeText);
                const remaining = bookingCount - 1;
                btimeEl.querySelector('.mr').textContent = remaining > 0 ? `+${remaining}` : '';
                // data-more 只服务一条 CSS 规则：菱形 marker 内容框只有 56px，带 `+N` 时本行
                // 实测 58.5px，图标必须让位（见 ark-floor-plan.css 那条规则的算式）。
                btimeEl.dataset.more = String(remaining > 0);
                if (bookingTone) btimeEl.dataset.tone = String(bookingTone);
                else delete btimeEl.dataset.tone;
                btimeEl.removeAttribute('hidden');
            } else {
                btimeEl.querySelector('.tm').textContent = '';
                btimeEl.querySelector('.mr').textContent = '';
                delete btimeEl.dataset.tone;
                btimeEl.dataset.more = 'false';
                btimeEl.setAttribute('hidden', '');
            }
        }

        // 预订提示块第 ② 行：客人姓名 —— 由 C# 侧决定填不填（点单台号页只在临近窗口内填）。
        const bookingEl = inner.querySelector('.table-booking');
        if (bookingEl) {
            let label = '';
            if (hasBooking && this.config.showBookingName && bookingName && !bookingIconOnly) {
                label = String(bookingName);
                if (this.config.showBookingPhoneTail && bookingPhoneTail) {
                    label += `·${bookingPhoneTail}`;
                }
            }
            if (label) {
                bookingEl.textContent = label;
                bookingEl.removeAttribute('hidden');
            } else {
                bookingEl.textContent = '';
                bookingEl.setAttribute('hidden', '');
            }
        }

        const bcalEl = inner.querySelector('.table-bcal');
        if (bcalEl) {
            if (hasBooking && bookingIconOnly) {
                if (bookingTone) bcalEl.dataset.tone = String(bookingTone);
                else delete bcalEl.dataset.tone;
                bcalEl.removeAttribute('hidden');
            } else {
                delete bcalEl.dataset.tone;
                bcalEl.setAttribute('hidden', '');
            }
        }

        // 用时排版方式，三选一（各自的几何理由见 ark-floor-plan.css 用时段）：
        //   inline 菱形恒定 —— 底部尖角放不下，随主体组居中；
        //   flow   有预订行 —— 4 行主体组整格居中会与浮在底部的用时重叠，退回流式贴底；
        //   float  其余     —— 用时浮在底部，台号 / 人数金额按整格居中。
        // ⚠ 必须是一个属性写一个值：拆成两个布尔时，前一个在两种方式下都为 true，CSS 规则会跨方式漏过去
        //   （2026-09-30 圆桌用时压到人数行上即此）。
        // hasBookingLines 与 TablePlanSelector.RenderGridTile 同口径（网格那面有预订行时用时挪到顶部）。
        if (contentEl) {
            const hasBookingLines = (btimeEl && !btimeEl.hidden) || (bookingEl && !bookingEl.hidden);
            const durationMode = !showDurationRow ? null
                : inner.dataset.shape === '2' ? 'inline'
                : hasBookingLines ? 'flow'
                : 'float';
            if (durationMode) contentEl.dataset.duration = durationMode;
            else delete contentEl.dataset.duration;
        }

        // 拖动启用 / 禁用（编辑模式下被锁则禁拖）
        if (this.config.editable && !isLocked) marker.dragging?.enable();
        else marker.dragging?.disable();
    }

    /**
     * 把当前 zoom 对应的 scale 写到容器 .ark-floor-plan 的 --scale 变量。
     * 所有 .ark-table 通过 CSS 变量继承读取 → 1 次 DOM 写入覆盖 N 个 marker。
     *
     * v3.36: 只在 Leaflet 视图真正变化时写（引擎落地的 setView、Leaflet 自身动画的 zoomanim）；
     *   手势与过渡期间桌台大小由 mapPane 的 scale 一并缩放，不逐帧写本变量。
     */
    _applyScaleToAllMarkers(zoomOverride) {
        if (!this.map || !this._container) return;
        const zoom = zoomOverride ?? this.map.getZoom() ?? 0;
        const scale = Math.max(this.config.minIconScale, Math.min(this.config.maxIconScale, Math.pow(2, zoom)));
        this._container.style.setProperty('--scale', String(scale));

        // 信息密度档：marker 整体跟随 transform: scale，缩到 0.7 以下时 11px 的预订姓名行
        // 实际渲染不足 8px，已不可读 —— 收起它，把仅剩的像素让给台号。
        // CSS 无法对 --scale 做条件判断（变量不能参与选择器比较），所以档位必须由 JS 写成属性。
        // 与 --scale 同样是容器级 1 次写入，不随桌台数量放大开销。
        const density = scale < this.config.bookingDensityMinScale ? 'min' : 'full';
        if (this._container.dataset.density !== density) {
            this._container.dataset.density = density;
        }
    }

    _createShapeSvg(shape, w, h) {
        // padding 按最大可能 stroke-width 预留（Reserved 状态 stroke=3）。
        // 所有 shape 内缩 padding=2，避免厚边被 SVG viewport 裁切。
        // stroke-width 由 CSS 按 [data-status] 动态控制（实心/空台 2 / Reserved 3）。
        const padding = 2;

        let shapePath;
        switch (shape) {
            case 1: // 圆形/椭圆
                const cx = w / 2;
                const cy = h / 2;
                const rx = (w / 2) - padding;
                const ry = (h / 2) - padding;
                shapePath = `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" />`;
                break;
            case 2: // 菱形
                const midX = w / 2;
                const midY = h / 2;
                shapePath = `<path d="M${midX} ${padding} L${w - padding} ${midY} L${midX} ${h - padding} L${padding} ${midY} Z" />`;
                break;
            default: // 矩形
                const rectW = w - 2 * padding;
                const rectH = h - 2 * padding;
                shapePath = `<rect x="${padding}" y="${padding}" width="${rectW}" height="${rectH}" rx="6" ry="6" />`;
                break;
        }

        return `<svg class="table-shape" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">${shapePath}</svg>`;
    }

    _createBoundary() {
        if (!this.map) return;

        const w = this.config.width;
        const h = this.config.height;

        // v3.27.20: boundary 改用单一 svgOverlay 替代 12 个独立 polyline
        //   pan 时只 1 次 element transform（与 grid svgOverlay 同架构），消除多 path
        //   reflow 累积；POS 触屏设备移动画布时边框不再有跟随延迟
        //   样式完全交给 CSS 控制（不在 path 上 inline stroke 属性）
        const svgNS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        svg.setAttribute('width', String(w));
        svg.setAttribute('height', String(h));
        svg.classList.add('ark-floor-plan-boundary-overlay');

        // 4 条独立边线 path（编辑模式 CSS 隐藏 top/left）
        const sides = [
            { id: 'top',    d: `M 0 0 L ${w} 0` },
            { id: 'right',  d: `M ${w} 0 L ${w} ${h}` },
            { id: 'bottom', d: `M 0 ${h} L ${w} ${h}` },
            { id: 'left',   d: `M 0 0 L 0 ${h}` }
        ];
        sides.forEach(s => {
            const p = document.createElementNS(svgNS, 'path');
            p.setAttribute('d', s.d);
            p.setAttribute('fill', 'none');
            p.classList.add('ark-floor-plan-boundary', `ark-floor-plan-boundary-${s.id}`);
            svg.appendChild(p);
        });

        // 4 个角的 L 形（一笔画 path，编辑模式 CSS 隐藏 tl/tr/bl）
        const cz = 28;
        const corners = [
            { id: 'tl', d: `M 0 ${cz} L 0 0 L ${cz} 0` },
            { id: 'tr', d: `M ${w - cz} 0 L ${w} 0 L ${w} ${cz}` },
            { id: 'br', d: `M ${w} ${h - cz} L ${w} ${h} L ${w - cz} ${h}` },
            { id: 'bl', d: `M ${cz} ${h} L 0 ${h} L 0 ${h - cz}` }
        ];
        corners.forEach(c => {
            const p = document.createElementNS(svgNS, 'path');
            p.setAttribute('d', c.d);
            p.setAttribute('fill', 'none');
            p.classList.add('ark-floor-plan-corner', `ark-floor-plan-corner-${c.id}`);
            svg.appendChild(p);
        });

        const bounds = [[0, 0], [h, w]];
        this.boundaryLayer = L.svgOverlay(svg, bounds, { interactive: false, pane: 'arkAbove' });
        this.boundaryLayer.addTo(this.map);

        // v3.27.20: boundaryLines / boundaryCorners 字段已不再使用，但保留为空数组兼容旧引用
        this.boundaryLines = [];
        this.boundaryCorners = [];

        // 网格在最底（boundary 之上一档）；底纹永远最底
        if (this.gridLayer) this.gridLayer.bringToBack();
        this._sinkBackdrop();
    }

    _bringBoundaryToFront() {
        if (this.boundaryLayer) {
            this.boundaryLayer.bringToFront();
        }
    }

    _bindMapEvents() {
        // 缩放事件路径（v3.36）:
        //   1) 引擎（pinch / 滚轮 / 双击 / 程序化过渡）：过程中不派发任何 Leaflet 事件，落地时一次
        //      setView(animate:false) → zoom → zoomend / moveend，在这里写 --scale、发通知
        //   2) Leaflet 自身的动画缩放（仅剩键盘 +/- 与 Shift 框选缩放）：zoomanim 拿到目标 zoom 写 --scale，
        //      .ark-table 在 .leaflet-zoom-anim 下随 Leaflet 的 250ms 过渡同步缩放（ark-floor-plan.css）
        this.map.on('zoomanim', (e) => {
            this._applyScaleToAllMarkers(e.zoom);
            this._repositionFloatingToolbar();
        });

        this.map.on('zoom', () => {
            this._applyScaleToAllMarkers();
            this._repositionFloatingToolbar();
        });

        this.map.on('zoomend', () => {
            this._applyScaleToAllMarkers();
            this._queueViewNotify(true);
            this._repositionHandles();
            this._repositionFloatingToolbar();
        });

        this.map.on('moveend', () => {
            this._queueViewNotify(false);
            // v3.23: 平移时同步 resize handle 屏幕位置
            this._repositionHandles();
            this._repositionFloatingToolbar();
        });

        // v3.36: 双击 / 双击轻点放大（Shift 缩小），以点击处为锚走引擎过渡
        this.map.on('dblclick', (e) => this._zlDoubleClick(e));

        // v3.15: 点空白处 → 清空选中
        // v3.20: 框选 mouseup 后浏览器仍会 fire click 事件并冒泡到 leaflet，
        //        通过 _suppressMapClickUntil 时间戳跳过该次 click，避免框选完立刻清空
        this.map.on('click', (e) => {
            if (this._suppressMapClickUntil && Date.now() < this._suppressMapClickUntil) return;
            if (this.selectedTableIds.size === 0) return;
            this.setSelectedTables([]);
            this._flushViewNotify();
            this._notify('OnTableClicked', {
                tableId: '',
                code: '',
                status: 0,
                isMulti: false,
                selectedIds: []
            });
        });

        // v3.15: 拖框选 —— map 容器空白处按住拖出选区矩形
        if (this.config.editable) {
            this._bindRubberbandEvents();
        }
    }

    // ========== v3.36: 单层变换缩放引擎（pinch / 滚轮 / 双击 / 程序化视图过渡共用）==========
    /**
     * 取代 v3.29「每帧 fire('zoomanim') 驱动各图层」的合成引擎。
     *
     * 手势与过渡期间（_zl.live）**不碰 Leaflet 状态**，只给 .leaflet-map-pane 写
     *   transform: translate3d(T) scale(s)（transform-origin 0 0）
     * 坐标关系：容器坐标 = T + s × 图层坐标；图层坐标是落地时 Leaflet 的 layerPoint（_zl.z0 级），
     * 桌台 / 背景图 / 底纹 / 边框作为 mapPane 的子层一起变换 —— 每帧 1 次 DOM 写入，与桌台数无关，
     * 桌台与地面之间也没有逐个取整带来的相对抖动。画面静止后 _zlCommit 一次 setView 写回，
     * 桌台在这一帧按新 --scale 重绘清晰（手势中放大时先发虚，与 v3.29 / 浏览器原生捏合一致）。
     *
     * v3.29 的缺陷（2026-10-16 生产 bundle 无头实测，长期结论见 客户端界面/设计方案.md §三.4「缩放手势」）：
     *   ① 锚点每帧取「当前两指中点下的内容」，两指一起平移时画面按 −(s−1) 倍反向走 →
     *      现在起手记住捏住的内容 A（图层坐标），每帧 T = mid − A·s，捏合与平移同时跟手；
     *   ② 起手调公开 map.stop()，zoomSnap:0 下它触发 moveend（OnViewChanged 跨 .NET 整页重渲）+ viewreset
     *      （全部图层重算）→ 现在起手只静默停掉 Leaflet 的回弹动画（_zlHaltPan）；
     *   ③ 上下限与画布边每帧硬夹 → 现在缩放越界有阻力（_zlRubber）、平移手势期不夹，
     *      松手以最后指位为锚，按 --ark-dur-base / --ark-ease-enter 回到边界（_zlSettle）。
     *
     * 与 Leaflet 的分工：单指拖动（非 live 时按下）仍归 Leaflet Draggable；live 期间按下的手指一律归本引擎
     *   （capture 阶段 stopPropagation 挡住 Draggable._onDown —— 它的拖动基准是未落地前的旧 pane 位置）。
     */
    _bindSmoothZoom() {
        const container = this._container;
        if (!container || !this.map) return;

        this.map.getPane('mapPane').style.transformOrigin = '0 0';
        this._zl = {
            live: false,        // mapPane 上有未写回 Leaflet 的变换
            z0: 0,              // 本轮 live 开始时 Leaflet 的 zoom（图层坐标的级别）
            s: 1,               // 相对 z0 的缩放倍数
            T: L.point(0, 0),   // mapPane 平移（容器坐标）
            g: null,            // 进行中的输入：'pinch' | 'pan' | 'wheel' | null（null = 回位 / 等待落地）
            ids: [],            // 跟踪的 touch identifier
            A: null,            // 锚点内容（图层坐标）
            d0: 1,              // 捏合起手两指距离
            zr0: 0,             // 捏合起手时的「未加阻力」缩放
            focal: null,        // 最后的手指 / 光标位置（容器坐标），松手回位的锚
            rect: null,         // 手势期缓存的容器 rect，避免 touchmove 每帧 reflow
            label: null,        // 缩放百分比文本节点（ArkFloorPlan.razor 渲染）
            anim: 0, paintRaf: 0, commitT: 0,
            wz: 0, wt: 0, wRaf: 0, wIdle: 0,
            invalidate: false   // live 期间被推迟的 invalidateSize
        };

        this._onZlTouchStart = (e) => this._zlTouchStart(e);
        this._onZlTouchMove = (e) => this._zlTouchMove(e);
        this._onZlTouchEnd = (e) => this._zlTouchEnd(e);
        this._onZlWheel = (e) => this._zlWheel(e);
        this._onZlMouseDown = () => this._zlMouseDown();
        // touchstart 走 capture：live 期间按下的手指要在 Leaflet Draggable（bubble）之前接住
        container.addEventListener('touchstart', this._onZlTouchStart, { capture: true, passive: false });
        container.addEventListener('touchmove', this._onZlTouchMove, { passive: false });
        container.addEventListener('touchend', this._onZlTouchEnd);
        container.addEventListener('touchcancel', this._onZlTouchEnd);
        container.addEventListener('wheel', this._onZlWheel, { passive: false });
        container.addEventListener('mousedown', this._onZlMouseDown, { capture: true });
    }

    _zlZoom() { return this._zl.z0 + Math.log2(this._zl.s); }

    _zlClampZoom(z) {
        return Math.max(this.map.getMinZoom(), Math.min(this.map.getMaxZoom(), z));
    }

    /** 越界阻力：超出部分 x 映射为 R·x/(x+R)，渐近 ZOOM_RUBBER 级 */
    _zlRubber(zr) {
        const a = this.map.getMinZoom(), b = this.map.getMaxZoom(), R = ZOOM_RUBBER;
        if (zr > b) { const x = zr - b; return b + R * x / (x + R); }
        if (zr < a) { const x = a - zr; return a - R * x / (x + R); }
        return zr;
    }

    /** _zlRubber 的逆：越界状态下再次起手捏合时，从画面上的缩放还原「未加阻力」的值 */
    _zlUnrubber(z) {
        const a = this.map.getMinZoom(), b = this.map.getMaxZoom(), R = ZOOM_RUBBER;
        if (z > b) { const y = Math.min(z - b, R * 0.98); return b + R * y / (R - y); }
        if (z < a) { const y = Math.min(a - z, R * 0.98); return a - R * y / (R - y); }
        return z;
    }

    /** 回位 / 视图过渡时长 = 动效 token --ark-dur-base；关闭动效（含系统减少动态效果）时为 0 → 瞬时落位 */
    _zlDuration() {
        const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ark-dur-base'));
        return Number.isFinite(v) ? v : 150;
    }

    /**
     * 静默停掉 Leaflet 的 pan 动画（越界回弹 / 程序化平移），不派发 'end'。
     * ⚠ 不能用 map._stop() / map.stop()：PosAnimation.stop → 'end' → moveend → _panInsideMaxBounds
     *   见画面仍在界外会立刻再开一段回弹（2026-10-16 实测）。越界量由本引擎松手回位一并收回。
     */
    _zlHaltPan() {
        const a = this.map?._panAnim;
        if (!a || !a._inProgress) return;
        L.Util.cancelAnimFrame(a._animId);
        a._inProgress = false;
        L.DomUtil.removeClass(this.map.getPane('mapPane'), 'leaflet-pan-anim');
    }

    _zlBegin() {
        const z = this._zl;
        if (z.live) return;
        this._zlHaltPan();
        z.z0 = this.map.getZoom();
        z.T = L.DomUtil.getPosition(this.map.getPane('mapPane')).clone();
        z.s = 1;
        z.live = true;
        z.label = this._container.querySelector('.ark-floor-plan-zoom-label');
        this._container.classList.add('ark-fp-live');
        // 手势中不跨 .NET：等落地后由 moveend 重新起计时（待发的标记保留）
        clearTimeout(this._viewNotifyTimer);
        this._viewNotifyTimer = 0;
        // 浮动工具栏按 Leaflet 坐标定位，live 期间不准，先藏起来，落地后由 zoomend / moveend 复位
        this._hideFloatingToolbar();
    }

    _zlPaint() {
        const z = this._zl;
        this.map.getPane('mapPane').style.transform = `translate3d(${z.T.x}px, ${z.T.y}px, 0) scale(${z.s})`;
        // 百分比直接改 Blazor 渲染的那个文本节点（Blazor 持有同一节点，落地后同步最终值时照常覆盖）
        const t = z.label?.firstChild;
        if (t && t.nodeType === 3) t.nodeValue = `${Math.round(Math.pow(2, this._zlZoom()) * 100)}%`;
    }

    /** touchmove 高频：每帧最多画一次 */
    _zlPaintSoon() {
        const z = this._zl;
        if (z.paintRaf) return;
        z.paintRaf = requestAnimationFrame(() => {
            z.paintRaf = 0;
            if (z.live) this._zlPaint();
        });
    }

    _zlStopMotion() {
        const z = this._zl;
        if (z.anim) { cancelAnimationFrame(z.anim); z.anim = 0; }
        clearTimeout(z.commitT); z.commitT = 0;
        if (z.wRaf) { cancelAnimationFrame(z.wRaf); z.wRaf = 0; }
        clearTimeout(z.wIdle); z.wIdle = 0;
        if (z.g === 'wheel') z.g = null;
    }

    _zlPoint(touch) {
        const r = this._zl.rect;
        return L.point(touch.clientX - r.left, touch.clientY - r.top);
    }

    _zlTouchById(list, id) {
        for (let i = 0; i < list.length; i++) {
            if (list[i].identifier === id) return list[i];
        }
        return null;
    }

    _zlSetupPinch(t1, t2) {
        const z = this._zl;
        const p1 = this._zlPoint(t1), p2 = this._zlPoint(t2);
        const mid = p1.add(p2).divideBy(2);
        z.g = 'pinch';
        z.ids = [t1.identifier, t2.identifier];
        z.d0 = Math.max(1, p1.distanceTo(p2));
        z.A = mid.subtract(z.T).divideBy(z.s);      // 起手时捏住的那块内容
        z.zr0 = this._zlUnrubber(this._zlZoom());
        z.focal = mid;
    }

    _zlSetupPan(touch) {
        const z = this._zl;
        const p = this._zlPoint(touch);
        z.g = 'pan';
        z.ids = [touch.identifier];
        z.A = p.subtract(z.T).divideBy(z.s);
        z.focal = p;
    }

    _zlTouchStart(e) {
        if (!this.map || this.map._animatingZoom) return;
        const z = this._zl;
        const n = e.touches.length;
        if (n >= 2) {
            // 单指拖动途中落下第二指：先让 Leaflet 把拖动收尾（同 Draggable._onDown 多指分支的做法）
            const d = this.map.dragging?._draggable;
            if (d && L.Draggable._dragging === d) d.finishDrag();
            this._zlStopMotion();
            this._zlBegin();
            this._zlHaltPan();          // 收尾的 moveend → _panInsideMaxBounds 可能刚拉起一段回弹
            z.rect = this._container.getBoundingClientRect();
            this._zlSetupPinch(e.touches[0], e.touches[1]);
            e.preventDefault();
            e.stopPropagation();
        } else if (n === 1 && z.live) {
            // 回位途中 / 落地前又按下：从当前画面接着拖（不 preventDefault，轻点桌台仍会产生 click）
            this._zlStopMotion();
            z.rect = this._container.getBoundingClientRect();
            this._zlSetupPan(e.touches[0]);
            e.stopPropagation();
        }
    }

    _zlTouchMove(e) {
        const z = this._zl;
        if (!z.live || (z.g !== 'pinch' && z.g !== 'pan')) return;
        e.preventDefault();
        if (z.g === 'pinch') {
            const t1 = this._zlTouchById(e.touches, z.ids[0]);
            const t2 = this._zlTouchById(e.touches, z.ids[1]);
            if (!t1 || !t2) return;
            const p1 = this._zlPoint(t1), p2 = this._zlPoint(t2);
            const mid = p1.add(p2).divideBy(2);
            const zoom = this._zlRubber(z.zr0 + Math.log2(Math.max(1, p1.distanceTo(p2)) / z.d0));
            z.s = Math.pow(2, zoom - z.z0);
            z.T = mid.subtract(z.A.multiplyBy(z.s));    // 捏住的内容放在当前两指中点
            z.focal = mid;
        } else {
            const t = this._zlTouchById(e.touches, z.ids[0]);
            if (!t) return;
            const p = this._zlPoint(t);
            z.T = p.subtract(z.A.multiplyBy(z.s));
            z.focal = p;
        }
        this._zlPaintSoon();
    }

    _zlTouchEnd(e) {
        const z = this._zl;
        if (!z.live || (z.g !== 'pinch' && z.g !== 'pan')) return;
        const ts = e.touches;
        if (ts.length >= 2) this._zlSetupPinch(ts[0], ts[1]);
        else if (ts.length === 1) this._zlSetupPan(ts[0]);   // 抬起一指：剩下那根接着拖
        else this._zlRelease();
    }

    _zlRelease() {
        const z = this._zl;
        z.g = null;
        if (z.paintRaf) { cancelAnimationFrame(z.paintRaf); z.paintRaf = 0; this._zlPaint(); }
        this._zlSettle();
    }

    /** 平移越界修正：手势期不夹，只在回位 / 过渡的目标上算一次（与单指拖动「自由拖出、松手弹回」一致） */
    _zlClampPan(T, s, zoom) {
        const map = this.map;
        if (!map.options.maxBounds) return T;
        const Lc = map.getSize().divideBy(2).subtract(T).divideBy(s);
        const c = map.layerPointToLatLng(Lc);
        const lim = map._limitCenter(c, zoom, map.options.maxBounds);
        if (lim.equals(c)) return T;
        return T.subtract(map.latLngToLayerPoint(lim).subtract(Lc).multiplyBy(s));
    }

    /** 以最后的手指 / 光标位置为锚，把越界量收回（zoomWant：滚轮的目标级别） */
    _zlSettle(zoomWant) {
        const z = this._zl;
        const zt = this._zlClampZoom(zoomWant !== undefined ? zoomWant : this._zlZoom());
        const f = z.focal || this.map.getSize().divideBy(2);
        const A = f.subtract(z.T).divideBy(z.s);
        const st = Math.pow(2, zt - z.z0);
        const Tt = this._zlClampPan(f.subtract(A.multiplyBy(st)), st, zt);
        this._zlAnimate(A, f, Tt.add(A.multiplyBy(st)), st, this._zlDuration());
    }

    /**
     * 锚点内容 A（图层坐标）的屏幕位置从 F0 走到 F1，缩放倍数按对数插值到 s1；结束后等待落地。
     * 纯缩放时 F0 = F1（锚点不动），纯平移时 s 不变。
     */
    _zlAnimate(A, F0, F1, s1, duration) {
        const z = this._zl;
        const s0 = z.s;
        if (duration <= 0 || (Math.abs(Math.log2(s1 / s0)) < 1e-4 && F0.distanceTo(F1) < 0.5)) {
            z.s = s1;
            z.T = F1.subtract(A.multiplyBy(s1));
            this._zlPaint();
            this._zlCommitSoon();
            return;
        }
        const t0 = performance.now();
        const step = (now) => {
            const k = Math.min(1, (now - t0) / duration);
            const e = easeEnter(k);
            z.s = s0 * Math.pow(s1 / s0, e);
            z.T = F0.add(F1.subtract(F0).multiplyBy(e)).subtract(A.multiplyBy(z.s));
            this._zlPaint();
            if (k < 1) z.anim = requestAnimationFrame(step);
            else { z.anim = 0; this._zlCommitSoon(); }
        };
        z.anim = requestAnimationFrame(step);
    }

    /** 静止后再写回：最后一帧先上屏，O(N) 的写回帧不和动画抢；期间再触摸直接从当前画面继续 */
    _zlCommitSoon() {
        const z = this._zl;
        clearTimeout(z.commitT);
        z.commitT = setTimeout(() => this._zlCommit(), 60);
    }

    _zlCommit() {
        const z = this._zl;
        clearTimeout(z.commitT); z.commitT = 0;
        if (!z.live || z.g || !this.map) return;
        const zoom = this._zlZoom();
        z.live = false;
        if (Math.abs(zoom - z.z0) < 1e-6) {
            // 纯平移：原样落位（不经 panBy 的取整），图层坐标不变
            L.DomUtil.setPosition(this.map.getPane('mapPane'), z.T);
            this.map.fire('move').fire('moveend');
        } else {
            // _resetView 把 mapPane 复位到 (0,0) 并按新级别重排各图层，同一任务内完成 → 同一帧上屏
            const center = this.map.layerPointToLatLng(this.map.getSize().divideBy(2).subtract(z.T).divideBy(z.s));
            this.map.setView(center, zoom, { animate: false });
        }
        this._container?.classList.remove('ark-fp-live');
        if (z.invalidate) { z.invalidate = false; this._scheduleInvalidate(); }
    }

    /**
     * 立即结束进行中的手势 / 过渡并写回（切楼层、改画布尺寸、外部 setView / getView 前调用）。
     * 越界部分按回位后的值落地；按着的手指此后被忽略，直到全部抬起。
     */
    _zoomSettleNow() {
        const z = this._zl;
        if (!z || !z.live || !this.map) return;
        this._zlStopMotion();
        if (z.paintRaf) { cancelAnimationFrame(z.paintRaf); z.paintRaf = 0; }
        z.g = null;
        const zt = this._zlClampZoom(this._zlZoom());
        const st = Math.pow(2, zt - z.z0);
        const half = this.map.getSize().divideBy(2);
        const A = half.subtract(z.T).divideBy(z.s);
        z.T = this._zlClampPan(half.subtract(A.multiplyBy(st)), st, zt);
        z.s = st;
        this._zlCommit();
    }

    /** 以容器点 focal 为锚缩放到 zoom（双击、zoomIn / zoomOut / setZoom） */
    _zlZoomAround(focal, zoom) {
        if (!this.map || this.map._animatingZoom) return;
        const z = this._zl;
        this._zlStopMotion();
        this._zlBegin();
        z.g = null;
        const zt = this._zlClampZoom(zoom);
        const A = focal.subtract(z.T).divideBy(z.s);
        const st = Math.pow(2, zt - z.z0);
        const Tt = this._zlClampPan(focal.subtract(A.multiplyBy(st)), st, zt);
        z.focal = focal;
        this._zlAnimate(A, focal, Tt.add(A.multiplyBy(st)), st, this._zlDuration());
    }

    /** 过渡到「center 居中、zoom 级」（resetView / focusTable / fitToContainer） */
    _zlViewTo(center, zoom) {
        if (!this.map || this.map._animatingZoom) return;
        const z = this._zl;
        this._zlStopMotion();
        this._zlBegin();
        z.g = null;
        const zt = this._zlClampZoom(zoom);
        const st = Math.pow(2, zt - z.z0);
        const Lt = this.map.latLngToLayerPoint(center);
        const Tt = this._zlClampPan(this.map.getSize().divideBy(2).subtract(Lt.multiplyBy(st)), st, zt);
        this._zlAnimate(Lt, z.T.add(Lt.multiplyBy(z.s)), Tt.add(Lt.multiplyBy(st)), st, this._zlDuration());
    }

    _zlDoubleClick(e) {
        const delta = this.map.options.zoomDelta;
        this._zlZoomAround(e.containerPoint, this.getZoom() + (e.originalEvent?.shiftKey ? -delta : delta));
    }

    /** 滚轮 / 触摸板捏合（Chromium 以 ctrl+wheel 下发）：目标级别指数逼近，静默 160ms 后回位落地 */
    _zlWheel(e) {
        e.preventDefault();
        const z = this._zl;
        if (!this.map || this.map._animatingZoom || z.g === 'pinch' || z.g === 'pan') return;
        if (!z.live) this._zlBegin();
        if (z.g !== 'wheel') {
            this._zlStopMotion();
            z.g = 'wheel';
            z.wz = this._zlZoom();
            z.wt = z.wz;
        }
        z.rect = this._container.getBoundingClientRect();
        const f = L.point(e.clientX - z.rect.left, e.clientY - z.rect.top);
        z.focal = f;
        z.A = f.subtract(z.T).divideBy(z.s);
        // deltaMode: 0=px / 1=line(估 16px) / 2=page；≈120px 滚动 = 1 级，向上滚放大
        const px = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this._container.clientHeight : 1);
        z.wt = this._zlClampZoom(z.wt - px / 120);
        clearTimeout(z.wIdle);
        z.wIdle = setTimeout(() => {
            z.wIdle = 0;
            if (z.wRaf) { cancelAnimationFrame(z.wRaf); z.wRaf = 0; }
            z.g = null;
            this._zlSettle(z.wt);
        }, 160);
        if (z.wRaf) return;
        const step = () => {
            if (z.g !== 'wheel') { z.wRaf = 0; return; }
            z.wz += (z.wt - z.wz) * 0.2;
            if (Math.abs(z.wt - z.wz) < 0.001) z.wz = z.wt;
            z.s = Math.pow(2, z.wz - z.z0);
            z.T = z.focal.subtract(z.A.multiplyBy(z.s));
            this._zlPaint();
            z.wRaf = requestAnimationFrame(step);
        };
        z.wRaf = requestAnimationFrame(step);
    }

    /** 鼠标按下时若还没落地：先落地，再让 Leaflet 的拖动 / 框选 / 画布拉伸接手 */
    _zlMouseDown() {
        const z = this._zl;
        if (z?.live && z.g !== 'pinch' && z.g !== 'pan') this._zoomSettleNow();
    }

    /** 解绑引擎（destroy 时调，须在 _container 置空前）*/
    _unbindSmoothZoom() {
        const c = this._container;
        if (c) {
            if (this._onZlTouchStart) c.removeEventListener('touchstart', this._onZlTouchStart, { capture: true });
            if (this._onZlTouchMove) c.removeEventListener('touchmove', this._onZlTouchMove);
            if (this._onZlTouchEnd) {
                c.removeEventListener('touchend', this._onZlTouchEnd);
                c.removeEventListener('touchcancel', this._onZlTouchEnd);
            }
            if (this._onZlWheel) c.removeEventListener('wheel', this._onZlWheel);
            if (this._onZlMouseDown) c.removeEventListener('mousedown', this._onZlMouseDown, { capture: true });
            c.classList.remove('ark-fp-live');
        }
        if (this._zl) {
            this._zlStopMotion();
            if (this._zl.paintRaf) cancelAnimationFrame(this._zl.paintRaf);
            this._zl = null;
        }
        this._cancelViewNotify();
        this._onZlTouchStart = null;
        this._onZlTouchMove = null;
        this._onZlTouchEnd = null;
        this._onZlWheel = null;
        this._onZlMouseDown = null;
    }

    // ========== v3.36: 视图通知合并发送 ==========
    /**
     * OnViewChanged / OnZoomChanged 静默 VIEW_NOTIFY_DELAY 后合并发一次。
     * 原因：OnViewChange 是 EventCallback，会让宿主（TablePlanSelector）整页重渲；
     *   Blazor Hybrid 在安卓 App 的 UI 线程上渲染，而 WebView 的触摸分发与出帧都经过这条线程 ——
     *   连续捏几下时，上一次的通知不能压在下一次的起手帧上。
     * 补发入口：点桌台 / 点空白清选中 / 框选结束（_flushViewNotify 后再发 OnTableClicked）、画布外按下。
     */
    _queueViewNotify(zoomChanged) {
        if (zoomChanged) this._pendingZoomNotify = true;
        this._pendingViewNotify = true;
        clearTimeout(this._viewNotifyTimer);
        this._viewNotifyTimer = this._zl?.live ? 0 : setTimeout(() => this._flushViewNotify(), VIEW_NOTIFY_DELAY);
    }

    _flushViewNotify() {
        clearTimeout(this._viewNotifyTimer);
        this._viewNotifyTimer = 0;
        if (!this.map) { this._cancelViewNotify(); return; }
        if (this._pendingZoomNotify) {
            this._pendingZoomNotify = false;
            this._notify('OnZoomChanged', { zoom: this.map.getZoom(), zoomPercent: Math.round(Math.pow(2, this.map.getZoom()) * 100) });
        }
        if (this._pendingViewNotify) {
            this._pendingViewNotify = false;
            const center = this.map.getCenter();
            this._notify('OnViewChanged', { centerX: center.lng, centerY: center.lat, zoom: this.map.getZoom() });
        }
    }

    _cancelViewNotify() {
        clearTimeout(this._viewNotifyTimer);
        this._viewNotifyTimer = 0;
        this._pendingZoomNotify = false;
        this._pendingViewNotify = false;
    }

    /**
     * v3.15: 拖框选事件绑定（仅编辑模式）
     * 监听 leaflet map 容器 mousedown：若 e.target 不在 marker 上 → 启动框选并 stopPropagation 阻止 leaflet pan
     */
    _bindRubberbandEvents() {
        const container = this._container;
        if (!container) return;

        const onMouseDown = (e) => {
            // 只响应左键，且 target 不在 marker / 缩放控件 / 网格 svg 等可交互层上
            if (e.button !== 0) return;
            if (e.target.closest('.ark-floor-plan-marker, .ark-floor-plan-zoom, button')) return;

            // v3.20: 仅在持续多选模式下启动框选（取消 Shift/Ctrl 修饰键，与"取消全部键盘快捷键"目标一致）
            if (this._persistentMultiMode !== true) return;

            // 启动框选
            const rect = container.getBoundingClientRect();
            const startX = e.clientX - rect.left;
            const startY = e.clientY - rect.top;
            this._rubberband = {
                startX, startY,
                // v3.20: 持续多选模式下框选 = union（保留已选 + 添加框中），与点桌台 toggle 累加语义一致
                shift: true,
                el: null
            };
            // 阻止 leaflet 把它当 pan 的起点
            e.stopPropagation();
            e.preventDefault();

            const onMove = (ev) => this._onRubberbandMove(ev, container);
            const onUp = (ev) => {
                this._onRubberbandUp(ev, container);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        };

        container.addEventListener('mousedown', onMouseDown, { capture: true });
        this._rubberbandMouseDown = onMouseDown;
    }

    _onRubberbandMove(e, container) {
        const rb = this._rubberband;
        if (!rb) return;
        const rect = container.getBoundingClientRect();
        const curX = e.clientX - rect.left;
        const curY = e.clientY - rect.top;

        // 首次移动才创建 div（避免单击产生空 div）
        if (!rb.el) {
            const moved = Math.abs(curX - rb.startX) > 3 || Math.abs(curY - rb.startY) > 3;
            if (!moved) return;
            const el = document.createElement('div');
            el.className = 'ark-rubberband';
            el.style.cssText = 'position:absolute;border:1px dashed #3b82f6;background:rgba(59,130,246,0.1);pointer-events:none;z-index:1000;';
            container.appendChild(el);
            rb.el = el;
            // v3.19: 记录框选起点时的已选集合（union 模式 base set）
            rb.baseSet = new Set(this.selectedTableIds);
        }

        const x = Math.min(curX, rb.startX);
        const y = Math.min(curY, rb.startY);
        const w = Math.abs(curX - rb.startX);
        const h = Math.abs(curY - rb.startY);
        rb.el.style.left = x + 'px';
        rb.el.style.top = y + 'px';
        rb.el.style.width = w + 'px';
        rb.el.style.height = h + 'px';
        rb.lastX = curX;
        rb.lastY = curY;

        // v3.19: 实时计算重叠 + 更新选中（Windows 资源管理器风格 marquee）
        this._updateRubberbandSelection(rb, container, { left: x, top: y, right: x + w, bottom: y + h });
    }

    /**
     * v3.19: 实时计算选区与每个 marker 的 AABB 重叠，更新 selectedTableIds
     * Shift/Ctrl 或持续多选模式 → union（保留 base + 框中）；否则 replace
     */
    _updateRubberbandSelection(rb, container, sel) {
        const rect = container.getBoundingClientRect();
        const hitIds = new Set();
        this.markers.forEach((marker) => {
            const el = marker.getElement();
            if (!el) return;
            const mRect = el.getBoundingClientRect();
            const mLeft = mRect.left - rect.left;
            const mTop = mRect.top - rect.top;
            const mRight = mLeft + mRect.width;
            const mBottom = mTop + mRect.height;
            const overlap = !(mRight < sel.left || mLeft > sel.right || mBottom < sel.top || mTop > sel.bottom);
            if (overlap) hitIds.add(String(marker._tableData.id));
        });

        let newSet;
        if (rb.shift && rb.baseSet) {
            newSet = new Set(rb.baseSet);
            hitIds.forEach(id => newSet.add(id));
        } else {
            newSet = hitIds;
        }

        // 仅当集合真正变化时才刷新视觉，避免每帧重渲所有 marker
        if (!this._setEquals(this.selectedTableIds, newSet)) {
            this.setSelectedTables(Array.from(newSet));
        }
    }

    _setEquals(a, b) {
        if (a.size !== b.size) return false;
        for (const x of a) if (!b.has(x)) return false;
        return true;
    }

    _onRubberbandUp(e, container) {
        const rb = this._rubberband;
        if (!rb) return;
        this._rubberband = null;

        // 没有真正拖出矩形（只是单击空白）—— 不动选区
        if (!rb.el) return;
        rb.el.remove();

        // v3.20: 框选刚结束，浏览器接着 fire click → 冒泡到 leaflet 触发 map.click 清空选中。
        //        设置时间窗（200ms）让 map.click 跳过本次合成 click。
        this._suppressMapClickUntil = Date.now() + 200;

        // v3.19: 选中已在 mousemove 期间持续更新；这里只 notify .NET 端最终选中集合
        this._flushViewNotify();
        this._notify('OnTableClicked', {
            tableId: '',
            code: '',
            status: 0,
            isMulti: true,
            selectedIds: Array.from(this.selectedTableIds)
        });
    }

    _bindMarkerEvents(marker) {
        marker.on('mousedown touchstart', (e) => {
            this._dragStart = {
                x: e.originalEvent?.clientX ?? e.originalEvent?.touches?.[0]?.clientX,
                y: e.originalEvent?.clientY ?? e.originalEvent?.touches?.[0]?.clientY,
                time: Date.now(),
                dragged: false
            };
            this._dragOriginalPos = {
                x: marker._tableData.x,
                y: marker._tableData.y
            };
            // v3.12: 重置上轮拖动残留的"最后无碰撞位置"
            this._dragLastSafe = null;
            // v3.27: 同样重置组级 last safe，避免不同次拖动间残留
            this._dragLastSafeGroup = null;

            // v3.15: 多选拖动准备 —— 当前 marker 在多选集合内 + 集合 > 1 → 记录组锚点
            const id = String(marker._tableData.id);
            if (this.selectedTableIds.size > 1 && this.selectedTableIds.has(id)) {
                this._dragGroupOrigin = new Map();
                this.selectedTableIds.forEach(sid => {
                    const m = this.markers.get(sid);
                    if (m) this._dragGroupOrigin.set(sid, { x: m._tableData.x, y: m._tableData.y });
                });
            } else {
                this._dragGroupOrigin = null;
            }
        });

        marker.on('drag', () => {
            if (this._dragStart) this._dragStart.dragged = true;
            // v3.27.22: 拖动期间隐藏浮动工具栏，避免跟随闪烁；dragend 时由 _repositionFloatingToolbar 复位
            this._hideFloatingToolbar();

            let { lat, lng } = marker.getLatLng();
            const tableId = marker._tableData.id;
            const tableData = marker._tableData;

            // 1. 边界约束
            const constrained = this._constrainPosition(lng, lat, tableData);
            lng = constrained.x;
            lat = constrained.y;

            // 2. 智能磁吸对齐 —— v3.12: 磁吸目标若会碰撞则放弃磁吸，避免吸到红框位置
            //    v3.27: 组拖动时改用整组测试（_groupOffsetCollides），磁吸把任一成员拽进碰撞即放弃
            //    v3.30: 磁吸先于网格吸附计算（对象对齐 > 网格对齐，Figma 同款优先级）——
            //    修"贴边点落在网格点 8~12px 环内时先被网格拽走、磁吸够不着"的死区
            const snapResult = this._calculateSmartSnap(tableId, lng, lat, tableData);
            let snapWouldCollide = false;
            if (snapResult.guides && snapResult.guides.length > 0) {
                if (this._dragGroupOrigin) {
                    const anchor = this._dragGroupOrigin.get(tableId);
                    if (anchor) {
                        snapWouldCollide = this._groupOffsetCollides(
                            snapResult.x - anchor.x,
                            snapResult.y - anchor.y);
                    }
                } else {
                    snapWouldCollide = this._hasCollisionWith(
                        tableId, snapResult.x, snapResult.y, tableData);
                }
            }
            let snappedX = false;
            let snappedY = false;
            if (!snapWouldCollide) {
                snappedX = snapResult.snappedX;
                snappedY = snapResult.snappedY;
                lng = snapResult.x;
                lat = snapResult.y;
                this._drawAlignmentGuides(snapResult.guides);
            } else {
                this._drawAlignmentGuides([]);
            }

            // 3. v3.16: 网格 snap-to-grid（拖近网格点时吸附；按 Alt 临时禁用）
            //    v3.30: 仅对未被磁吸命中的轴生效（磁吸优先）；阈值按屏幕像素语义随缩放换算
            if (this.config.snapToGrid && !this._altPressed) {
                const grid = this.config.gridSize || 80;
                const threshold = this._snapThresholdCanvas(this.config.snapToGridThreshold);
                if (!snappedX) {
                    const sx = Math.round(lng / grid) * grid;
                    if (Math.abs(sx - lng) < threshold) lng = sx;
                }
                if (!snappedY) {
                    const sy = Math.round(lat / grid) * grid;
                    if (Math.abs(sy - lat) < threshold) lat = sy;
                }
            }

            // 4. v3.13: 硬墙 —— 理想位置碰撞时不让 marker 进入
            //    单选走单 marker 测试；v3.27: 组拖动改走"组级硬墙"（任一成员撞外都不放行）
            //    v3.30: "回退 last 采样位置"改为"夹紧到接触点 + 轴向滑墙"——快拖单帧冲进
            //    邻桌内部也贴墙停下；旧行为最终间距=上一帧空隙（取决于鼠标步长），靠不到最近
            if (this._dragGroupOrigin) {
                // ---- v3.27: 组级硬墙 ----
                const anchor = this._dragGroupOrigin.get(tableId);
                if (anchor) {
                    let dx = lng - anchor.x;
                    let dy = lat - anchor.y;

                    if (this._groupOffsetCollides(dx, dy)) {
                        const last = this._dragLastSafeGroup ?? { dx: 0, dy: 0 };
                        if (this._groupOffsetCollides(last.dx, last.dy)) {
                            // last 本身已撞（起点即重叠等脏数据），保持旧"原地不动"行为
                            dx = last.dx;
                            dy = last.dy;
                        } else {
                            // 沿运动方向夹紧到接触点，再对残余位移轴向滑墙（X 优先，与单选一致）
                            let c = this._clampGroupOffsetToContact(last.dx, last.dy, dx, dy);
                            if (!this._groupOffsetCollides(dx, c.dy)) {
                                c = { dx, dy: c.dy };
                            } else {
                                c = this._clampGroupOffsetToContact(c.dx, c.dy, dx, c.dy);
                            }
                            if (!this._groupOffsetCollides(c.dx, dy)) {
                                c = { dx: c.dx, dy };
                            } else {
                                c = this._clampGroupOffsetToContact(c.dx, c.dy, c.dx, dy);
                            }
                            dx = c.dx;
                            dy = c.dy;
                        }
                        // 反写主 marker 位置，保证视觉跟组同步停在墙边
                        lng = anchor.x + dx;
                        lat = anchor.y + dy;
                    }
                    this._dragLastSafeGroup = { dx, dy };

                    // 同步组内非主成员
                    this._dragGroupOrigin.forEach((aOrigin, otherId) => {
                        if (otherId === tableId) return;
                        const otherM = this.markers.get(otherId);
                        if (!otherM) return;
                        const c = this._constrainPosition(aOrigin.x + dx, aOrigin.y + dy, otherM._tableData);
                        otherM.setLatLng([c.y, c.x]);
                    });
                }
            } else if (this._hasCollisionWith(tableId, lng, lat, tableData)) {
                // ---- 单选硬墙 ----
                // v3.30: 交给 _slideToContact 贴墙解算（先夹紧到接触点，再轴向滑墙 X 优先），
                // 取代"被堵轴回退 last"——回退版最终间距=上一帧空隙，是"靠不到最近"的主根因
                const last = this._dragLastSafe
                          ?? this._dragOriginalPos
                          ?? { x: marker._tableData.x, y: marker._tableData.y };
                const c = this._slideToContact(tableId, last.x, last.y, lng, lat, tableData);
                lng = c.x;
                lat = c.y;
            }

            // 此时 (lng, lat) 一定 safe；记录给 dragend 兜底
            this._dragLastSafe = { x: lng, y: lat };
            // 红框警告 drag 中永不触发；保留调用作 dragend 防御层重置（防止上次遗留）
            this._setCollisionWarning(marker, false);

            // 5. 更新位置
            //    v3.27.2: 回退 v3.27.1 的 Leaflet draggable 内部状态校准 ——
            //    grid snap (阈值 12px) 每帧产生小 delta，累加到 _startPos 后导致
            //    鼠标位移 < 阈值时 marker 永久卡在 snap 点（用户体验"严重依赖吸附"）。
            //    Leaflet 的 _onDrag → 我们的 handler → setLatLng(safe) 都在同一 rAF
            //    回调内同步执行，浏览器只 paint 最终的 safe 位置；dragend 时 _onUp 会
            //    cancelAnimFrame，最后的 mouse-based _newPos 不会渲染，marker._latlng
            //    保留为我们的 safe 值。所以无需操作 Leaflet 内部状态。
            marker.setLatLng([lat, lng]);
        });

        marker.on('dragend', () => {
            this._clearAlignmentGuides();

            const { lat, lng } = marker.getLatLng();
            const tableId = marker._tableData.id;
            const tableData = marker._tableData;

            // v3.27: 组拖动走组级三级兜底；单选保持 v3.12 行为
            if (this._dragGroupOrigin) {
                const anchor = this._dragGroupOrigin.get(tableId);
                let dx = anchor ? lng - anchor.x : 0;
                let dy = anchor ? lat - anchor.y : 0;

                if (this._groupOffsetCollides(dx, dy)) {
                    // v3.30: 兜底链首加"从最后安全组位移向落点方向夹紧"（与拖动视觉一致），
                    // last 缺失或本身碰撞再走 v3.27 环式搜索 / 起点链路
                    const last = this._dragLastSafeGroup;
                    let safe = null;
                    if (last && !this._groupOffsetCollides(last.dx, last.dy)) {
                        safe = this._clampGroupOffsetToContact(last.dx, last.dy, dx, dy);
                    }
                    safe = safe
                        ?? this._findNearestSafeGroupOffset(dx, dy)
                        ?? this._dragLastSafeGroup
                        ?? { dx: 0, dy: 0 };
                    dx = safe.dx;
                    dy = safe.dy;
                }

                const items = [];
                this._dragGroupOrigin.forEach((aOrigin, otherId) => {
                    const m = this.markers.get(otherId);
                    if (!m) return;
                    const c = this._constrainPosition(aOrigin.x + dx, aOrigin.y + dy, m._tableData);
                    // 同单选：组拖落点同样取整，避免长小数入库
                    c.x = Math.round(c.x);
                    c.y = Math.round(c.y);
                    // v3.30: round 亚像素重叠复检修正（仅对组外检测——组内相对位置由同一 dx/dy
                    // + 整数起点保持一致，不会因 round 互相错位）
                    if (this._hasCollisionWithOutsideGroup(otherId, c.x, c.y, m._tableData)) {
                        const fixed = this._nudgeRoundedSafe(c.x, c.y, m._tableData,
                            (tx, ty) => this._hasCollisionWithOutsideGroup(otherId, tx, ty, m._tableData));
                        if (fixed) { c.x = fixed.x; c.y = fixed.y; }
                    }
                    m._tableData.x = c.x;
                    m._tableData.y = c.y;
                    m.setLatLng([c.y, c.x]);
                    this._setCollisionWarning(m, false);
                    items.push({
                        tableId: otherId, x: c.x, y: c.y,
                        originalX: aOrigin.x, originalY: aOrigin.y
                    });
                });
                this._dragGroupOrigin = null;
                this._dragLastSafeGroup = null;
                this._notify('OnTablesMoved', { items });
            } else {
                let finalX = lng;
                let finalY = lat;

                if (this._hasCollisionWith(tableId, lng, lat, tableData)) {
                    // v3.30: 兜底链首加"从拖动期间最后安全点向落点方向夹紧"（结果与拖动视觉一致）；
                    // lastSafe 缺失或本身碰撞再走 v3.12 环式搜索 / 起点链路
                    let safePos = null;
                    if (this._dragLastSafe
                        && !this._hasCollisionWith(tableId, this._dragLastSafe.x, this._dragLastSafe.y, tableData)) {
                        safePos = this._clampToContact(tableId,
                            this._dragLastSafe.x, this._dragLastSafe.y, lng, lat, tableData);
                    }
                    safePos = safePos
                        ?? this._findNearestSafePosition(tableId, lng, lat, tableData)
                        ?? this._dragLastSafe
                        ?? this._dragOriginalPos;
                    if (safePos) {
                        finalX = safePos.x;
                        finalY = safePos.y;
                    }
                }

                // v3.27.x: 像素坐标取整再落库 —— 落点来自 Leaflet 反投影，zoomSnap:0 的
                // 分数 zoom 下为 15~17 位长小数；桌台布局无需亚像素精度，round 保持 DB 干净
                finalX = Math.round(finalX);
                finalY = Math.round(finalY);

                // v3.30: round 可能把奇数尺寸贴边中心（x.5）取整出 0.5px 重叠——复检并在
                // 1px 邻位内修正，避免脏坐标入库漂移后续拖动的碰撞基准
                if (this._hasCollisionWith(tableId, finalX, finalY, tableData)) {
                    const fixed = this._nudgeRoundedSafe(finalX, finalY, tableData,
                        (tx, ty) => this._hasCollisionWith(tableId, tx, ty, tableData));
                    if (fixed) { finalX = fixed.x; finalY = fixed.y; }
                }

                marker.setLatLng([finalY, finalX]);
                marker._tableData.x = finalX;
                marker._tableData.y = finalY;
                this._setCollisionWarning(marker, false);

                this._notify('OnTableMoved', {
                    tableId,
                    x: finalX,
                    y: finalY
                });
            }

            this._dragOriginalPos = null;
            this._dragLastSafe = null;

            // v3.27.22: 拖动结束 → 浮动工具栏跟到新位置
            this._repositionFloatingToolbar();
        });

        marker.on('click', (e) => {
            if (this._dragStart?.dragged) {
                this._dragStart = null;
                return;
            }

            const tableData = marker._tableData;
            // v3.20: 多选触发仅靠"持续多选模式"开关，取消 Shift/Ctrl 修饰键
            const isMulti = this._persistentMultiMode === true;

            // v3.15: 多选累加 / 单选替换 —— 直接更新内部 set，不等 .NET 回调减少抖动
            const id = String(tableData.id);
            if (isMulti) {
                if (this.selectedTableIds.has(id)) this.selectedTableIds.delete(id);
                else this.selectedTableIds.add(id);
            } else {
                this.selectedTableIds = new Set([id]);
            }
            this.selectedTableId = id;
            // 刷新所有 marker 视觉态（开销 = O(N) class toggle，可接受）
            this.markers.forEach(m => this._applyVisualState(m));

            // v3.36: 点桌台常紧接着进点单页（组件销毁）—— 先补发待发的视图通知，保证视图记忆是最后一次手势后的
            this._flushViewNotify();
            this._notify('OnTableClicked', {
                tableId: tableData.id,
                code: tableData.code,
                status: marker._status,
                isMulti,
                selectedIds: Array.from(this.selectedTableIds)
            });
            this._dragStart = null;
        });
    }

    /**
     * v3.2: 边界约束
     * 直接使用画布坐标，不受缩放影响
     */
    _constrainPosition(x, y, tableData) {
        const w = tableData.w || this.config.defaultTableW;
        const h = tableData.h || this.config.defaultTableH;

        const halfW = w / 2;
        const halfH = h / 2;

        const minX = halfW;
        const maxX = this.config.width - halfW;
        x = Math.max(minX, Math.min(maxX, x));

        const minY = halfH;
        const maxY = this.config.height - halfH;
        y = Math.max(minY, Math.min(maxY, y));

        return { x, y };
    }

    /**
     * v3.30: 吸附阈值统一按"屏幕像素"语义换算为画布单位。
     * zoom<0（缩小）时画布阈值放大、zoom>0 时缩小，保证不同缩放级别下吸附手感一致
     * （旧版直接用画布单位，50% 缩放时等效捕获带折半，贴边更难）。
     */
    _snapThresholdCanvas(px) {
        const zoom = this.map?.getZoom() ?? 0;
        return px / Math.pow(2, zoom);
    }

    /**
     * v3.2: 智能磁吸对齐（直接使用画布坐标）
     * v3.30:
     * - 阈值走 _snapThresholdCanvas（屏幕像素语义）
     * - "贴边"配对（left↔right / top↔bottom）要求正交投影邻近（间隙 ≤ 1.5×较大尺寸）
     *   才参与竞争，防止被画布另一头桌台的对齐线劫持；命中后距离×EDGE_WEIGHT 让
     *   "贴住眼前这张桌"优先于"和远处桌对齐"
     * - 返回 snappedX / snappedY 供 drag 管线对已磁吸的轴跳过网格吸附
     */
    _calculateSmartSnap(tableId, x, y, tableData) {
        const threshold = this._snapThresholdCanvas(this.config.snapThreshold);
        const EDGE_WEIGHT = 0.8;
        const w = tableData.w || this.config.defaultTableW;
        const h = tableData.h || this.config.defaultTableH;

        const halfW = w / 2;
        const halfH = h / 2;

        const guides = [];
        let snapX = null;
        let snapY = null;
        let minDistX = threshold;
        let minDistY = threshold;

        const current = {
            left: x - halfW,
            right: x + halfW,
            centerX: x,
            top: y - halfH,
            bottom: y + halfH,
            centerY: y
        };

        for (const [id, marker] of this.markers) {
            if (id === tableId) continue;

            const otherData = marker._tableData;
            const otherW = otherData.w || this.config.defaultTableW;
            const otherH = otherData.h || this.config.defaultTableH;
            const otherHalfW = otherW / 2;
            const otherHalfH = otherH / 2;

            const target = {
                left: otherData.x - otherHalfW,
                right: otherData.x + otherHalfW,
                centerX: otherData.x,
                top: otherData.y - otherHalfH,
                bottom: otherData.y + otherHalfH,
                centerY: otherData.y
            };

            // v3.30: 正交方向间隙（<0 = 投影重叠）——限定"贴边"候选的参与范围
            const orthoGapY = Math.abs(y - otherData.y) - (halfH + otherHalfH);
            const orthoGapX = Math.abs(x - otherData.x) - (halfW + otherHalfW);

            // X 轴对齐（edge=贴边配对：我左贴他右 / 我右贴他左）
            const xPairs = [
                { cur: 'left', tgt: 'left' },
                { cur: 'left', tgt: 'right', edge: true },
                { cur: 'centerX', tgt: 'centerX' },
                { cur: 'right', tgt: 'left', edge: true },
                { cur: 'right', tgt: 'right' }
            ];

            for (const pair of xPairs) {
                if (pair.edge && orthoGapY > 1.5 * Math.max(h, otherH)) continue;
                let dist = Math.abs(current[pair.cur] - target[pair.tgt]);
                if (pair.edge) dist *= EDGE_WEIGHT;
                if (dist < minDistX) {
                    minDistX = dist;
                    const offset = current[pair.cur] - x;
                    snapX = target[pair.tgt] - offset;
                    this._pendingGuideX = {
                        x: target[pair.tgt],
                        y1: Math.min(current.top, target.top) - 20,
                        y2: Math.max(current.bottom, target.bottom) + 20
                    };
                }
            }

            // Y 轴对齐（edge=贴边配对：我上贴他下 / 我下贴他上）
            const yPairs = [
                { cur: 'top', tgt: 'top' },
                { cur: 'top', tgt: 'bottom', edge: true },
                { cur: 'centerY', tgt: 'centerY' },
                { cur: 'bottom', tgt: 'top', edge: true },
                { cur: 'bottom', tgt: 'bottom' }
            ];

            for (const pair of yPairs) {
                if (pair.edge && orthoGapX > 1.5 * Math.max(w, otherW)) continue;
                let dist = Math.abs(current[pair.cur] - target[pair.tgt]);
                if (pair.edge) dist *= EDGE_WEIGHT;
                if (dist < minDistY) {
                    minDistY = dist;
                    const offset = current[pair.cur] - y;
                    snapY = target[pair.tgt] - offset;
                    this._pendingGuideY = {
                        y: target[pair.tgt],
                        x1: Math.min(current.left, target.left) - 20,
                        x2: Math.max(current.right, target.right) + 20
                    };
                }
            }
        }

        const resultX = snapX !== null ? snapX : x;
        const resultY = snapY !== null ? snapY : y;

        if (snapX !== null && this._pendingGuideX) {
            guides.push({ type: 'vertical', ...this._pendingGuideX });
        }
        if (snapY !== null && this._pendingGuideY) {
            guides.push({ type: 'horizontal', ...this._pendingGuideY });
        }

        // v3.30: snappedX/snappedY 供 drag 管线对已磁吸的轴跳过网格吸附
        return { x: resultX, y: resultY, guides, snappedX: snapX !== null, snappedY: snapY !== null };
    }

    _drawAlignmentGuides(guides) {
        this._clearAlignmentGuides();

        if (!guides || guides.length === 0) return;

        this._alignmentGuides = [];

        for (const guide of guides) {
            let line;
            if (guide.type === 'vertical') {
                line = L.polyline(
                    [[guide.y1, guide.x], [guide.y2, guide.x]],
                    {
                        color: '#ff00ff',
                        weight: 1,
                        dashArray: '4, 4',
                        opacity: 0.9,
                        interactive: false,
                        className: 'ark-alignment-guide',
                        pane: 'arkAbove'
                    }
                );
            } else {
                line = L.polyline(
                    [[guide.y, guide.x1], [guide.y, guide.x2]],
                    {
                        color: '#ff00ff',
                        weight: 1,
                        dashArray: '4, 4',
                        opacity: 0.9,
                        interactive: false,
                        className: 'ark-alignment-guide',
                        pane: 'arkAbove'
                    }
                );
            }
            line.addTo(this.map);
            this._alignmentGuides.push(line);
        }
    }

    _clearAlignmentGuides() {
        if (this._alignmentGuides) {
            this._alignmentGuides.forEach(line => {
                if (this.map) this.map.removeLayer(line);
            });
            this._alignmentGuides = [];
        }
        this._pendingGuideX = null;
        this._pendingGuideY = null;
    }

    _findNearestSafePosition(tableId, x, y, tableData) {
        if (!this._hasCollisionWith(tableId, x, y, tableData)) {
            return { x, y };
        }

        // v3.12: step 与桌台尺寸成比例（避免大桌台时颗粒度过细），maxDist 覆盖到画布对角线一半
        const w = tableData.w || this.config.defaultTableW;
        const h = tableData.h || this.config.defaultTableH;
        const step = Math.max(8, Math.max(w, h) / 4);
        const maxDist = Math.hypot(this.config.width, this.config.height) / 2;

        const directions = [
            [1, 0], [-1, 0], [0, 1], [0, -1],
            [1, 1], [1, -1], [-1, 1], [-1, -1]
        ];

        for (let dist = step; dist <= maxDist; dist += step) {
            for (const [dx, dy] of directions) {
                const testX = x + dx * dist;
                const testY = y + dy * dist;

                const constrained = this._constrainPosition(testX, testY, tableData);
                if (Math.abs(constrained.x - testX) > 1 || Math.abs(constrained.y - testY) > 1) {
                    continue;
                }

                if (!this._hasCollisionWith(tableId, constrained.x, constrained.y, tableData)) {
                    return { x: constrained.x, y: constrained.y };
                }
            }
        }

        return null;
    }

    _setCollisionWarning(marker, hasCollision) {
        const el = marker.getElement();
        if (!el) return;

        const inner = el.querySelector('.table-marker');
        if (!inner) return;

        if (hasCollision) {
            inner.classList.add('collision');
        } else {
            inner.classList.remove('collision');
        }
    }

    _notify(method, args) {
        if (!this.dotnetRef) return;
        this.dotnetRef.invokeMethodAsync(method, args).catch(err => {
            console.warn(`[ArkFloorPlan] ${method} callback failed:`, err);
        });
    }

    // ==================== 浮动工具栏（v3.27.22） ====================
    /**
     * C# 端 attach DOM 引用 + 关联 tableId；本类只负责定位（不渲染按钮、不处理点击）。
     * 定位规则：marker 上方居中，gap=12px；上方溢出翻下方；横向钳制到画布 4px 内。
     * 高频钩子（zoomanim/moveend/dragend）调 _repositionFloatingToolbar 重算 transform。
     * 拖动期间（drag handler 内）调 _hideFloatingToolbar 隐藏，避免跟随闪烁。
     */
    attachFloatingToolbar(toolbarEl, tableId, kind = 'table') {
        // 宿主传来的不是元素（Blazor 空 ElementReference 序列化成普通对象）时不挂，免得定位时 style.left 报错
        if (!toolbarEl || !toolbarEl.style) return;
        // 2026-10-18: kind = 'decor' 时 tableId 是布景条目 id（布景模式的浮动工具栏）
        this._floatingToolbar = { el: toolbarEl, tableId: String(tableId), kind };
        this._repositionFloatingToolbar();
    }

    detachFloatingToolbar() {
        this._floatingToolbar = null;
    }

    /**
     * v3.27.23: 动态切换画布尺寸锁。
     * locked=true：移除 mousemove/mousedown 监听 + 重置 cursor，画布边缘拖拽完全失效
     * locked=false：仅当 editable 时重新挂载（保持 setEditable 的总开关语义）
     */
    setCanvasSizeLock(locked) {
        this.config.lockCanvasSize = !!locked;
        if (locked) {
            this._removeResizeHandles();
        } else if (this.config.editable) {
            this._createResizeHandles();
        }
    }

    _hideFloatingToolbar() {
        if (this._floatingToolbar?.el) {
            this._floatingToolbar.el.style.visibility = 'hidden';
            this._floatingToolbar.el.style.pointerEvents = 'none';
        }
    }

    _repositionFloatingToolbar() {
        const ft = this._floatingToolbar;
        if (!ft || !ft.el || !this.map) return;
        // v3.36: 缩放引擎未落地时 Leaflet 坐标是旧的，先藏；落地的 zoomend / moveend 再定位
        if (this._zl?.live) { this._hideFloatingToolbar(); return; }

        let centerX, topY, bottomY;
        if (ft.kind === 'decor') {
            const box = this._decorBoxes().find(b => b.id === ft.tableId);
            if (!box || this._decorEdit?.drag) {
                ft.el.style.visibility = 'hidden';
                ft.el.style.pointerEvents = 'none';
                return;
            }
            centerX = (box.l + box.r) / 2;
            topY = box.t;
            bottomY = box.b;
        } else {
            const marker = this.markers.get(ft.tableId);
            if (!marker || !marker._tableData) {
                ft.el.style.visibility = 'hidden';
                ft.el.style.pointerEvents = 'none';
                return;
            }

            const td = marker._tableData;
            const h = td.h || this.config.defaultTableH;
            // tableData.x/y = 桌台中心（画布坐标）
            centerX = td.x;
            topY = td.y - h / 2;
            bottomY = td.y + h / 2;
        }

        // 画布坐标 → 容器屏幕坐标
        const ptCenterTop = this.map.latLngToContainerPoint([topY, centerX]);
        const ptCenterBottom = this.map.latLngToContainerPoint([bottomY, centerX]);

        const tbW = ft.el.offsetWidth || 200;
        const tbH = ft.el.offsetHeight || 40;
        const gap = 12;

        const container = this.map.getContainer();
        const containerW = container.clientWidth;

        // 默认：marker 上方居中
        let left = ptCenterTop.x - tbW / 2;
        let top = ptCenterTop.y - tbH - gap;

        // 上方溢出 → 翻到下方
        if (top < 4) {
            top = ptCenterBottom.y + gap;
        }

        // 横向钳制（不溢出画布）
        if (left < 4) left = 4;
        if (left + tbW > containerW - 4) left = containerW - 4 - tbW;

        ft.el.style.left = `${left}px`;
        ft.el.style.top = `${top}px`;
        ft.el.style.visibility = 'visible';
        ft.el.style.pointerEvents = 'auto';
    }
}

// ==================== 全局 API ====================
const instances = new Map();

const ArkFloorPlan = {
    // 2026-10-27: 首绘一次带齐（建好再揭幕）—— 背景 / 初始视图 / 桌台随 create 一起来，同步建完，
    //   再等背景图解码（有上限）+ 两帧（marker 落位、首帧画出）后才返回。容器此时由 C# 的 data-fp-boot 压成透明，
    //   返回后 C# 摘掉该属性 → 整体淡入一次。这几项只在这里读一次，不进 config。
    //   消息按发送顺序处理：同步部分（建图 + 桌台）一定先于 .NET 之后发来的调用执行，等待期间到的增量推送照常落到 marker 上。
    async create(containerId, options, dotnetRef) {
        if (instances.has(containerId)) {
            instances.get(containerId).destroy();
        }
        const {
            backgroundUrl, backgroundWidth, backgroundHeight, backgroundOpacity,
            initialZoom, initialCenterX, initialCenterY,
            tables, statusMap, extraDataMap,
            ...config
        } = options || {};
        const instance = new FloorPlan(containerId, config);
        instance.init(dotnetRef);
        instances.set(containerId, instance);

        if (backgroundUrl) {
            instance.setBackground(backgroundUrl, {
                width: backgroundWidth,
                height: backgroundHeight,
                opacity: backgroundOpacity,
                instant: true
            });
        }
        if (typeof initialZoom === 'number' && typeof initialCenterX === 'number' && typeof initialCenterY === 'number') {
            instance.setView(initialZoom, initialCenterX, initialCenterY);
        }
        if (tables && tables.length) {
            instance.addTables(tables, statusMap || {}, extraDataMap || {});
        }

        await instance._awaitBackgroundDecoded(150);
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        return true;
    },

    destroy(containerId) {
        const instance = instances.get(containerId);
        if (instance) {
            instance.destroy();
            instances.delete(containerId);
        }
    },

    getInstance: (id) => instances.get(id),

    reinit: (id, options) => {
        const instance = instances.get(id);
        if (instance) {
            instance.reinit(options);
            return true;
        }
        return false;
    },

    updateBounds: (id, width, height) => {
        const instance = instances.get(id);
        if (instance) {
            instance.updateBounds(width, height);
            return true;
        }
        return false;
    },

    setBackground: (id, url, opts) => instances.get(id)?.setBackground(url, opts),
    setBackdrop: (id, token) => instances.get(id)?.setBackdrop(token),
    setDecor: (id, items) => instances.get(id)?.setDecor(items),

    // v3.28: 楼层原地切换（不销毁 L.map）
    switchFloor: (id, opts) => instances.get(id)?.switchFloor(opts),

    addTable: (id, table, status, extra) => instances.get(id)?.addTable(table, status, extra),
    addTables: (id, tables, statusMap, extraMap) => instances.get(id)?.addTables(tables, statusMap, extraMap),
    // 2026-10-27: 整表替换合成一次调用 —— clearTables / addTables 分两次过桥时，中间可能画出一帧「一张台都没有」
    setTables: (id, tables, statusMap, extraMap) => {
        const instance = instances.get(id);
        if (!instance) return;
        instance.clearTables();
        instance.addTables(tables, statusMap || {}, extraMap || {});
    },
    removeTable: (id, tableId) => instances.get(id)?.removeTable(tableId),
    clearTables: (id) => instances.get(id)?.clearTables(),
    updateTableStatus: (id, tableId, status, extra) => instances.get(id)?.updateTableStatus(tableId, status, extra),
    updateTableStatuses: (id, updates) => instances.get(id)?.updateTableStatuses(updates),
    updateTablePosition: (id, tableId, x, y) => instances.get(id)?.updateTablePosition(tableId, x, y),
    updateTableShape: (id, tableId, shape) => instances.get(id)?.updateTableShape(tableId, shape),

    setZoom: (id, zoom) => instances.get(id)?.setZoom(zoom),
    zoomIn: (id) => instances.get(id)?.zoomIn(),
    zoomOut: (id) => instances.get(id)?.zoomOut(),
    resetView: (id) => instances.get(id)?.resetView(),
    fitToContainer: (id) => instances.get(id)?.fitToContainer(),
    getZoom: (id) => instances.get(id)?.getZoom() ?? 0,
    getZoomPercentage: (id) => instances.get(id)?.getZoomPercentage() ?? 100,
    getView: (id) => instances.get(id)?.getView() ?? null,
    setView: (id, zoom, x, y) => instances.get(id)?.setView(zoom, x, y),
    focusTable: (id, tableId, zoom) => instances.get(id)?.focusTable(tableId, zoom),

    setEditable: (id, editable) => instances.get(id)?.setEditable(editable),
    toggleBoundary: (id, show) => instances.get(id)?.toggleBoundary(show),
    selectTable: (id, tableId) => instances.get(id)?.selectTable(tableId),
    setSelectedTables: (id, idArray) => instances.get(id)?.setSelectedTables(idArray ?? []),
    setPersistentMultiMode: (id, enabled) => instances.get(id)?.setPersistentMultiMode(!!enabled),

    checkCollisionAt: (id, tableId, x, y, w, h) =>
        instances.get(id)?.checkCollisionAt(tableId, x, y, w, h) ?? false,
    findSafePosition: (id, w, h, preferX, preferY) =>
        instances.get(id)?.findSafePosition(w, h, preferX, preferY) ?? { x: preferX, y: preferY, hasCollision: true },

    // v3.27.22: 浮动工具栏（C# 渲染 DOM + 点击逻辑；本层只管定位）
    attachFloatingToolbar: (id, toolbarEl, tableId, kind) =>
        instances.get(id)?.attachFloatingToolbar(toolbarEl, tableId, kind || 'table'),
    detachFloatingToolbar: (id) =>
        instances.get(id)?.detachFloatingToolbar(),

    // v3.27.23: 画布尺寸锁（背景图绑定时禁拖拽 resize）
    setCanvasSizeLock: (id, locked) =>
        instances.get(id)?.setCanvasSizeLock(locked),

    // 2026-10-18: Admin 素材缩略图（与画布同一份几何）
    paintSwatches: (rootEl) => paintSwatches(rootEl),

    // 2026-10-18: 布景编辑（只在 Admin 桌面启用）
    setDecorEditMode: (id, on) => setDecorEditMode(instances.get(id), !!on),
    selectDecor: (id, decorId) => selectDecor(instances.get(id), decorId || null)
};

window.ArkFloorPlan = ArkFloorPlan;

export default ArkFloorPlan;
